import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import PageLayout from "@/components/PageLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { 
  ArrowLeft, Phone, MapPin, Trash2, Save, Plus, X, 
  Package, AlertTriangle, Calendar, Clock, CheckCircle, CheckCircle2,
  Upload, FileText, User, Euro, Camera, Wrench, Eye, EyeOff,
  Paperclip, Download, List, CalendarPlus, Mail, RotateCcw
} from "lucide-react";
import { useSWRConfig } from "swr";
import { Textarea } from "@/components/ui/textarea";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { formatDataItaliana } from "@/utils/dateUtils";
import FileDropZone from "@/components/FileDropZone";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CommessaDetailPage({ user, logout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { mutate } = useSWRConfig();
  
  const [commessa, setCommessa] = useState(null);
  const [posatori, setPosatori] = useState([]);
  const [magazzini, setMagazzini] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Stati per le sezioni editabili
  const [articoli, setArticoli] = useState([]);
  const [noteGenerali, setNoteGenerali] = useState("");
  const [pagamentiStatus, setPagamentiStatus] = useState(null);
  const [pagamenti, setPagamenti] = useState({
    anticipo_status: "non_pagato",
    anticipo_importo: 0,
    saldo_residuo: 0
  });
  const [programmazione, setProgrammazione] = useState({
    tipo_posa: "singola",
    posatore_id: null,
    data_posa: "",
    ora_posa: "",
    note_posa: "",
    fasi: [],
    posato: false,
    articoli_ids: [], // Articoli selezionati per la posa
    articoli_completati: [] // Articoli flaggati come completati
  });
  const [creatingCalendarEvent, setCreatingCalendarEvent] = useState(false);
  const [chiusura, setChiusura] = useState({
    data_chiusura: new Date().toISOString().split('T')[0],
    note_finali: "",
    foto_cantiere: [],
    invia_recensione: false
  });
  const [comunicazioni, setComunicazioni] = useState({
    status_avviso_cliente: "da_avvisare",
    note_avviso_cliente: "",
    mail_dettagli_inviata: false,
    data_mail_dettagli: ""
  });
  const [trasporto, setTrasporto] = useState({
    facchinaggio_id: null,
    trasporta_posatore: false,
    data_trasporto: "",
    ora_trasporto: "",
    note_trasporto: ""
  });
  const [trasporti, setTrasporti] = useState([]);
  const [datiClientePagamenti, setDatiClientePagamenti] = useState(null);
  const [speseMargini, setSpeseMargini] = useState({
    spese: [],
    incassi: []
  });
  
  // Stato per le note di attenzione
  const [noteAttenzione, setNoteAttenzione] = useState([]);
  const [nuovaNota, setNuovaNota] = useState({
    nota: "",
    data: new Date().toISOString().split('T')[0],
    testo_libero: "",
    allegato: null
  });
  const [uploadingNota, setUploadingNota] = useState(false);
  
  // Stato per il visualizzatore del Registro Mail Inviate
  const [viewingMailLog, setViewingMailLog] = useState(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [commessaRes, posatoriRes, trasportiRes, magazziniRes] = await Promise.all([
        axios.get(`${API}/commesse-pose/${id}`, { withCredentials: true }),
        axios.get(`${API}/posatori`, { withCredentials: true }),
        axios.get(`${API}/trasporti`, { withCredentials: true }),
        axios.get(`${API}/magazzini`, { withCredentials: true })
      ]);
      
      const comm = commessaRes.data;
      setCommessa(comm);
      setPosatori(posatoriRes.data);
      setTrasporti(trasportiRes.data);
      setMagazzini(magazziniRes.data);
      
      // Inizializza stati
      setArticoli(comm.articoli || []);
      setNoteGenerali(comm.note_generali || "");
      setPagamenti({
        anticipo_status: comm.anticipo_status || "non_pagato",
        anticipo_importo: comm.anticipo_importo || 0,
        saldo_residuo: comm.saldo_residuo || 0
      });
      setProgrammazione({
        tipo_posa: comm.tipo_posa || "singola",
        posatore_id: comm.posatore_id || null,
        data_posa: comm.data_posa || "",
        ora_posa: comm.ora_posa || "",
        note_posa: comm.note_posa || "",
        fasi: comm.fasi || [],
        posato: comm.posato || false,
        articoli_ids: comm.articoli_posa || [], // Carica articoli programmati
        articoli_completati: comm.articoli_completati || [] // Carica articoli completati
      });
      setComunicazioni({
        status_avviso_cliente: comm.status_avviso_cliente || "da_avvisare",
        note_avviso_cliente: comm.note_avviso_cliente || "",
        mail_dettagli_inviata: comm.mail_dettagli_inviata || false,
        data_mail_dettagli: comm.data_mail_dettagli || ""
      });
      setTrasporto({
        facchinaggio_id: comm.facchinaggio_id || null,
        trasporta_posatore: comm.trasporta_posatore || false,
        data_trasporto: comm.data_trasporto || "",
        ora_trasporto: comm.ora_trasporto || "",
        note_trasporto: comm.note_trasporto || ""
      });
      setSpeseMargini({
        spese: comm.spese || [],
        incassi: comm.incassi || []
      });
      
      // Carica note di attenzione
      setNoteAttenzione(comm.note_attenzione || []);
      
      // Fetch status pagamenti
      try {
        const pagamentiRes = await axios.get(`${API}/pagamenti/verifica-cliente`, {
          params: {
            cliente_nome: comm.cliente_nome,
            cliente_cognome: comm.cliente_cognome
          },
          withCredentials: true
        });
        setPagamentiStatus(pagamentiRes.data);
      } catch (e) {
        console.error("Error fetching pagamenti status:", e);
      }
      
      // Fetch dati completi cliente da pagamenti
      try {
        const clientiRes = await axios.get(`${API}/pagamenti/clienti`, { withCredentials: true });
        const cliente = clientiRes.data.find(c => 
          c.nome === comm.cliente_nome && c.cognome === comm.cliente_cognome
        );
        if (cliente) {
          setDatiClientePagamenti(cliente);
        }
      } catch (e) {
        console.error("Error fetching dati cliente:", e);
      }
    } catch (e) {
      console.error("Error fetching data:", e);
      toast.error("Errore nel caricamento della commessa");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCommessa = async () => {
    if (!window.confirm(`Sei sicuro di voler eliminare la commessa di ${commessa.cliente_nome} ${commessa.cliente_cognome}?`)) return;
    
    try {
      await axios.delete(`${API}/commesse-pose/${id}`, { withCredentials: true });
      toast.success("Commessa eliminata");
      navigate("/commesse-pose");
    } catch (e) {
      toast.error("Errore nell'eliminazione della commessa");
    }
  };

  const handleSaveNoteGenerali = async () => {
    try {
      await axios.put(`${API}/commesse-pose/${id}/note-generali`, { note_generali: noteGenerali }, { withCredentials: true });
      toast.success("Note salvate");
    } catch (e) {
      toast.error("Errore nel salvataggio delle note");
    }
  };

  const handleSaveArticoli = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/commesse-pose/${id}/articoli`, { articoli }, { withCredentials: true });
      toast.success("Articoli aggiornati");
      fetchData();
    } catch (e) {
      toast.error("Errore nel salvataggio degli articoli");
    } finally {
      setSaving(false);
    }
  };

  const handleSavePagamenti = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/commesse-pose/${id}/pagamenti`, pagamenti, { withCredentials: true });
      toast.success("Pagamenti aggiornati");
      fetchData();
    } catch (e) {
      toast.error("Errore nel salvataggio dei pagamenti");
    } finally {
      setSaving(false);
    }
  };

  const handleRiapriPosa = async () => {
    if (!window.confirm("Sei sicuro di voler riaprire questa posa?\n\nVerranno azzerati: flag Posato, stato Completata, data completamento.\nGli alert torneranno visibili in dashboard.")) {
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/commesse-pose/${id}/riapri-posa`, {}, { withCredentials: true });
      toast.success("Posa riaperta con successo");
      // Ricarica commessa e programmazione
      fetchData();
      // Revalida cache SWR per aggiornare CommessePosePage e Dashboard
      try {
        const year = new Date().getFullYear();
        mutate((key) => typeof key === "string" && (
          key.startsWith("/commesse-pose") ||
          key.startsWith(`/commesse-pose/clienti-da-posare`) ||
          key.startsWith(`/commesse-pose/date-posa-scadute`)
        ));
      } catch (_) {}
    } catch (e) {
      console.error("Error riapri posa:", e);
      toast.error("Errore nella riapertura della posa");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveProgrammazione = async () => {
    // Validazioni
    if (programmazione.tipo_posa === "singola" && !programmazione.posatore_id) {
      toast.error("Seleziona un posatore");
      return;
    }
    
    // ALERT per note di attenzione non lette
    if (noteAttenzione.length > 0) {
      const nonLette = noteAttenzione.filter(n => !n.letta).length;
      if (nonLette > 0) {
        const conferma = window.confirm(
          `⚠️ ATTENZIONE!\n\nCi sono ${nonLette} nota/e nella sezione "Attenzione" che non hai ancora letto.\n\nTi consigliamo di leggere tutte le note prima di programmare la posa.\n\nVuoi continuare comunque?`
        );
        if (!conferma) {
          return;
        }
      } else {
        // Anche se tutte lette, avvisa comunque dell'esistenza delle note
        toast.info("📌 Ricorda di verificare la sezione 'Attenzione' prima della posa");
      }
    }
    
    if (pagamentiStatus && pagamentiStatus.residuo > 0) {
      if (!window.confirm("⚠️ Il saldo non è completato. Vuoi comunque programmare la posa?")) {
        return;
      }
    }
    
    const articoliTrasmessi = articoli.filter(a => a.trasmesso).length;
    if (articoliTrasmessi === 0) {
      toast.error("Non ci sono articoli trasmessi");
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(`${API}/commesse-pose/${id}/programmazione`, programmazione, { withCredentials: true });
      toast.success("Programmazione salvata");
      fetchData();
      // Revalida cache SWR per aggiornare CommessePosePage (Finiti/Deposito) e Dashboard (alerts)
      try {
        mutate((key) => typeof key === "string" && (
          key.startsWith("/commesse-pose") ||
          key.startsWith(`/commesse-pose/clienti-da-posare`) ||
          key.startsWith(`/commesse-pose/date-posa-scadute`)
        ));
      } catch (_) {}
    } catch (e) {
      toast.error("Errore nel salvataggio della programmazione");
    } finally {
      setSaving(false);
    }
  };

  // Mappatura posatori -> Calendar ID (stessa del backend)
  const POSATORI_CALENDAR_IDS = {
    "Ado": "74e082652e9a3b39256a9fdfd13efe1997f0fe0414312267431bb13eb9f4bb01@group.calendar.google.com",
    "Alessandro Mereu": "kkkdnp6627tc87cfkkhj8aeecc@group.calendar.google.com",
    "Antonio Filograno": "188004b884b9d8acc0e02b461a0540cea3794d0127d2be9799e1e3405c8c0561@group.calendar.google.com",
    "Ignazio Maxia": "v9f5l0saif80pmaq1p7j4vg6nk@group.calendar.google.com",
    "Matteo Scarcioni": "bfad732c43132d6a749dad8fbf4d77cddfc8b95977bc65d42031203ec152442a@group.calendar.google.com",
    "Simone Porcedda": "e51e56cd94f76a8727ddf7e8c4ef73843c71cc20f1ea9513a2a46743b2448c22@group.calendar.google.com"
  };

  // Mappatura trasportatori -> Calendar ID
  const TRASPORTI_CALENDAR_IDS = {
    "Ettore Manca Trasporti": "d1f0d25f0ab625a2c51d4db0e095b0d66c11c7ee77ee0fd0aafa93e6fa47cd81@group.calendar.google.com",
    "Marco Figus Trasporti": "f9b744854766954238a45e63474e781ed6b14d6ce787247f3bf260ff8293f438@group.calendar.google.com",
    "Vittorio Trasporti": "ea8c35daf555049a416303cd56b8c4a946e0fa6525e0e6d48d5a3ecdbac20cb7@group.calendar.google.com"
  };

  const handleCreateCalendarEvent = () => {
    // Validazioni
    if (!programmazione.posatore_id) {
      toast.error("Seleziona un posatore");
      return;
    }
    if (!programmazione.data_posa) {
      toast.error("Seleziona una data di posa");
      return;
    }
    if (!programmazione.ora_posa) {
      toast.error("Inserisci l'ora di posa");
      return;
    }
    
    // Trova il posatore
    const posatoreSelezionato = posatori.find(p => p.id === programmazione.posatore_id);
    if (!posatoreSelezionato) {
      toast.error("Posatore non trovato");
      return;
    }
    
    // Costruisci il nome completo del posatore
    const posatoreNomeCompleto = `${posatoreSelezionato.nome || ""} ${posatoreSelezionato.cognome || ""}`.trim();
    
    // Trova il calendar ID per questo posatore
    const calendarId = POSATORI_CALENDAR_IDS[posatoreNomeCompleto];
    if (!calendarId) {
      toast.error(`Calendario non configurato per il posatore: ${posatoreNomeCompleto}`);
      return;
    }
    
    // Prepara dati cliente
    const clienteNome = `${commessa.cliente_nome || ""} ${commessa.cliente_cognome || ""}`.trim();
    const indirizzo = commessa.indirizzo || "";
    const telefono = commessa.cliente_telefono || "";
    
    // Titolo evento: "Posatore Posa NomeCliente, Indirizzo, Telefono"
    const titolo = `${posatoreNomeCompleto} Posa ${clienteNome}, ${indirizzo}, ${telefono}`;
    
    // Prepara la descrizione con articoli e note
    let descrizione = "";
    
    // Aggiungi articoli da posare
    const articoliDaPosare = articoli
      .filter(art => {
        const articoloId = art.id || `art_${articoli.indexOf(art)}`;
        return Array.isArray(programmazione.articoli_ids) && programmazione.articoli_ids.includes(articoloId);
      })
      .map(art => {
        const nomeArticolo = art.nome_articolo || "Articolo";
        const codiceDdt = art.codice_ddt ? ` (DDT: ${art.codice_ddt})` : "";
        return `${nomeArticolo}${codiceDdt}`;
      });
    
    if (articoliDaPosare.length > 0) {
      descrizione += "ARTICOLI DA POSARE:\n";
      articoliDaPosare.forEach(art => {
        descrizione += `• ${art}\n`;
      });
      descrizione += "\n";
    }
    
    // Aggiungi note di posa
    if (programmazione.note_posa) {
      descrizione += "NOTE DI POSA:\n";
      descrizione += programmazione.note_posa;
    }
    
    // Formatta data e ora per Google Calendar (formato: YYYYMMDDTHHMMSS)
    const dataBase = programmazione.data_posa.replace(/-/g, ""); // YYYY-MM-DD -> YYYYMMDD
    const oraBase = programmazione.ora_posa.replace(":", "") + "00"; // HH:MM -> HHMMSS
    const dataInizio = `${dataBase}T${oraBase}`;
    
    // Durata di 2 ore per default
    const [ore, minuti] = programmazione.ora_posa.split(":").map(Number);
    const oreFine = String(ore + 2).padStart(2, "0");
    const oraFine = `${oreFine}${String(minuti).padStart(2, "0")}00`;
    const dataFine = `${dataBase}T${oraFine}`;
    
    // Costruisci l'URL di Google Calendar con il calendario specifico
    const params = new URLSearchParams({
      action: "TEMPLATE",
      text: titolo,
      dates: `${dataInizio}/${dataFine}`,
      details: descrizione || "Posa",
      location: indirizzo,
      src: calendarId
    });
    
    const calendarUrl = `https://calendar.google.com/calendar/render?${params.toString()}`;
    
    // Apri in una nuova finestra
    window.open(calendarUrl, "_blank");
    toast.success("Calendario aperto in una nuova finestra");
  };

  const handleSaveComunicazioni = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/commesse-pose/${id}/comunicazioni`, comunicazioni, { withCredentials: true });
      toast.success("Comunicazioni aggiornate");
      fetchData();
    } catch (e) {
      toast.error("Errore nel salvataggio delle comunicazioni");
    } finally {
      setSaving(false);
    }
  };

  const [sendingMailDettagli, setSendingMailDettagli] = useState(false);
  const [mailDraft, setMailDraft] = useState(null); // { payload, subject, body_html, body_text, to, bcc, articoli_count }
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Costruisce il payload comune a preview/invio
  const buildMailPayload = () => {
    const clienteEmail = commessa.cliente_email || datiClientePagamenti?.email;
    if (!clienteEmail) { toast.error("Email cliente non disponibile"); return null; }
    if (!programmazione.data_posa) { toast.error("Data posa non impostata"); return null; }
    if (!programmazione.posatore_id) { toast.error("Posatore non assegnato"); return null; }
    const posatoreSelezionato = posatori.find(p => p.id === programmazione.posatore_id);
    if (!posatoreSelezionato) { toast.error("Posatore non trovato"); return null; }
    let trasportatoreInfo = null;
    if (trasporto.facchinaggio_id) {
      const t = trasporti.find(x => x.id === trasporto.facchinaggio_id);
      if (t) trasportatoreInfo = { nome: t.nome || "", telefono: t.telefono || "" };
    }
    const formatDate = (dateStr) => {
      if (!dateStr) return "da definire";
      const date = new Date(dateStr);
      return date.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
    };
    return {
      cliente_email: clienteEmail,
      cliente_nome: `${commessa.cliente_nome || ""} ${commessa.cliente_cognome || ""}`.trim(),
      data_posa: formatDate(programmazione.data_posa),
      ora_posa: programmazione.ora_posa || "da definire",
      posatore_nome: `${posatoreSelezionato.nome || ""} ${posatoreSelezionato.cognome || ""}`.trim(),
      posatore_telefono: posatoreSelezionato.telefono || "non disponibile",
      trasportatore_nome: trasportatoreInfo?.nome || null,
      trasportatore_telefono: trasportatoreInfo?.telefono || null,
      data_trasporto: trasporto.data_trasporto ? formatDate(trasporto.data_trasporto) : null
    };
  };

  // Step 1: mostra preview editabile
  const handleInviaMailDettagli = async () => {
    const payload = buildMailPayload();
    if (!payload) return;
    setLoadingPreview(true);
    try {
      const resp = await axios.post(`${API}/commesse-pose/${id}/preview-mail-dettagli`, payload, { withCredentials: true });
      setMailDraft({
        payload,
        subject: resp.data.subject,
        body_html: resp.data.body_html,
        body_text: resp.data.body_text,
        to: resp.data.to,
        bcc: resp.data.bcc,
        articoli_count: resp.data.articoli_count
      });
    } catch (e) {
      console.error("preview error:", e);
      toast.error(e.response?.data?.detail || "Errore nel caricamento dell'anteprima");
    } finally {
      setLoadingPreview(false);
    }
  };

  // Step 2: conferma invio (con eventuali override)
  const confirmSendMailDettagli = async () => {
    if (!mailDraft) return;
    setSendingMailDettagli(true);
    try {
      const response = await axios.post(`${API}/commesse-pose/${id}/invia-mail-dettagli`, {
        ...mailDraft.payload,
        subject_override: mailDraft.subject,
        body_html_override: mailDraft.body_html,
        body_text_override: mailDraft.body_text
      }, { withCredentials: true });
      toast.success(response.data.message || "Bozza mail salvata con successo");
      setComunicazioni({
        ...comunicazioni,
        mail_dettagli_inviata: true,
        data_mail_dettagli: new Date().toISOString()
      });
      setMailDraft(null);
      fetchData();
    } catch (e) {
      console.error("Error sending mail dettagli:", e);
      if (e.response?.status === 401) {
        toast.error("Gmail non collegato. Vai in Scadenzario Pagamenti → Collega Gmail per autorizzare.");
      } else {
        toast.error(e.response?.data?.detail || "Errore nella creazione della bozza mail");
      }
    } finally {
      setSendingMailDettagli(false);
    }
  };

  const handleSaveTrasporto = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/commesse-pose/${id}/trasporto`, trasporto, { withCredentials: true });
      toast.success("Trasporto aggiornato");
      fetchData();
    } catch (e) {
      toast.error("Errore nel salvataggio del trasporto");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCalendarEventTrasporto = () => {
    // Validazioni
    if (!trasporto.facchinaggio_id) {
      toast.error("Seleziona un servizio di trasporto");
      return;
    }
    if (!trasporto.data_trasporto) {
      toast.error("Seleziona una data di trasporto");
      return;
    }
    
    // Trova il trasportatore selezionato
    const trasportoSelezionato = trasporti.find(t => t.id === trasporto.facchinaggio_id);
    if (!trasportoSelezionato) {
      toast.error("Servizio trasporto non trovato");
      return;
    }
    
    // Costruisci il nome del trasportatore
    const trasportoNome = trasportoSelezionato.nome || "";
    
    // Trova il calendar ID per questo trasportatore
    const calendarId = TRASPORTI_CALENDAR_IDS[trasportoNome];
    if (!calendarId) {
      toast.error(`Calendario non configurato per: ${trasportoNome}`);
      return;
    }
    
    // Prepara dati cliente
    const clienteNome = `${commessa.cliente_nome || ""} ${commessa.cliente_cognome || ""}`.trim();
    const indirizzo = commessa.indirizzo || "";
    const telefono = commessa.cliente_telefono || "";
    
    // Titolo evento: "Trasporto NomeCliente, Indirizzo, Telefono"
    const titolo = `Trasporto ${clienteNome}, ${indirizzo}, ${telefono}`;
    
    // Prepara la descrizione
    let descrizione = "";
    
    // Aggiungi info posatore se trasporta il posatore
    if (trasporto.trasporta_posatore && programmazione.posatore_id) {
      const posatore = posatori.find(p => p.id === programmazione.posatore_id);
      if (posatore) {
        descrizione += `TRASPORTA POSATORE: ${posatore.nome} ${posatore.cognome || ""}\n\n`;
      }
    }
    
    // Aggiungi note trasporto
    if (trasporto.note_trasporto) {
      descrizione += "NOTE TRASPORTO:\n";
      descrizione += trasporto.note_trasporto;
    }
    
    // Formatta data e ora per Google Calendar
    const dataBase = trasporto.data_trasporto.replace(/-/g, ""); // YYYY-MM-DD -> YYYYMMDD
    let dataInizio, dataFine;
    
    if (trasporto.ora_trasporto) {
      const oraBase = trasporto.ora_trasporto.replace(":", "") + "00"; // HH:MM -> HHMMSS
      dataInizio = `${dataBase}T${oraBase}`;
      
      // Durata di 2 ore per default
      const [ore, minuti] = trasporto.ora_trasporto.split(":").map(Number);
      const oreFine = String(ore + 2).padStart(2, "0");
      const oraFine = `${oreFine}${String(minuti).padStart(2, "0")}00`;
      dataFine = `${dataBase}T${oraFine}`;
    } else {
      // Evento giornata intera
      dataInizio = dataBase;
      dataFine = dataBase;
    }
    
    // Costruisci l'URL di Google Calendar con il calendario specifico
    const params = new URLSearchParams({
      action: "TEMPLATE",
      text: titolo,
      dates: `${dataInizio}/${dataFine}`,
      details: descrizione || "Trasporto",
      location: indirizzo,
      src: calendarId
    });
    
    const calendarUrl = `https://calendar.google.com/calendar/render?${params.toString()}`;
    
    // Apri in una nuova finestra
    window.open(calendarUrl, "_blank");
    toast.success("Calendario aperto in una nuova finestra");
  };

  const handleSaveSpeseMargin = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/commesse-pose/${id}/spese-margini`, speseMargini, { withCredentials: true });
      toast.success("Spese e Margini aggiornati");
      fetchData();
    } catch (e) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  // Gestione Spese
  const handleAggiungiSpesa = () => {
    const nuovaSpesa = {
      id: `spesa-${Date.now()}`,
      descrizione: "",
      importo_imponibile: 0,
      iva_percentuale: 22,
      importo_ivato: 0
    };
    setSpeseMargini({ ...speseMargini, spese: [...speseMargini.spese, nuovaSpesa] });
  };

  const handleUpdateSpesa = (id, field, value) => {
    const speseAggiornate = speseMargini.spese.map(s => {
      if (s.id === id) {
        const updated = { ...s, [field]: value };
        // Ricalcola importo ivato
        if (field === 'importo_imponibile' || field === 'iva_percentuale') {
          updated.importo_ivato = parseFloat(updated.importo_imponibile) * (1 + parseFloat(updated.iva_percentuale) / 100);
        }
        return updated;
      }
      return s;
    });
    setSpeseMargini({ ...speseMargini, spese: speseAggiornate });
  };

  const handleRimuoviSpesa = (id) => {
    setSpeseMargini({ ...speseMargini, spese: speseMargini.spese.filter(s => s.id !== id) });
  };

  // Gestione Incassi
  const handleAggiungiIncasso = () => {
    const nuovoIncasso = {
      id: `incasso-${Date.now()}`,
      descrizione: "",
      importo_imponibile: 0,
      iva_percentuale: 22,
      importo_ivato: 0
    };
    setSpeseMargini({ ...speseMargini, incassi: [...speseMargini.incassi, nuovoIncasso] });
  };

  const handleUpdateIncasso = (id, field, value) => {
    const incassiAggiornati = speseMargini.incassi.map(i => {
      if (i.id === id) {
        const updated = { ...i, [field]: value };
        // Ricalcola importo ivato
        if (field === 'importo_imponibile' || field === 'iva_percentuale') {
          updated.importo_ivato = parseFloat(updated.importo_imponibile) * (1 + parseFloat(updated.iva_percentuale) / 100);
        }
        return updated;
      }
      return i;
    });
    setSpeseMargini({ ...speseMargini, incassi: incassiAggiornati });
  };

  const handleRimuoviIncasso = (id) => {
    setSpeseMargini({ ...speseMargini, incassi: speseMargini.incassi.filter(i => i.id !== id) });
  };

  // Calcoli
  const calcolaTotaleSpese = () => {
    return speseMargini.spese.reduce((sum, s) => sum + parseFloat(s.importo_ivato || 0), 0);
  };

  const calcolaTotaleIncassi = () => {
    return speseMargini.incassi.reduce((sum, i) => sum + parseFloat(i.importo_ivato || 0), 0);
  };

  const calcolaBilancio = () => {
    return calcolaTotaleIncassi() - calcolaTotaleSpese();
  };

  const calcolaPercentualeProfitto = () => {
    const totaleIncassi = calcolaTotaleIncassi();
    if (totaleIncassi === 0) return 0;
    return ((calcolaBilancio() / totaleIncassi) * 100).toFixed(2);
  };

  const handleAggiungiArticolo = (index) => {
    const art = articoli[index];
    setArticoli(articoli.map((a, i) => 
      i === index ? { ...a, trasmesso: !a.trasmesso } : a
    ));
  };

  const handleAggiungiFase = () => {
    const nuovaFase = {
      id: `fase_${Date.now()}`,
      nome: `Fase ${programmazione.fasi.length + 1}`,
      posatore_id: null,
      posatore_nome: null,
      data: "",
      ora: "",
      completata: false,
      posato: false,
      tipo: "normale",
      file_rilievo: null,
      note: "",
      articoli_ids: [] // Inizializza array vuoto per articoli
    };
    setProgrammazione({
      ...programmazione,
      fasi: [...programmazione.fasi, nuovaFase]
    });
  };

  const handleRimuoviFase = async (faseId) => {
    if (!window.confirm("Sei sicuro di voler rimuovere questa fase?")) return;
    
    // Se la fase è già salvata nel DB
    if (!faseId.startsWith("fase_")) {
      try {
        await axios.delete(`${API}/commesse-pose/${id}/fasi/${faseId}`, { withCredentials: true });
        toast.success("Fase eliminata");
        fetchData();
      } catch (e) {
        toast.error("Errore nell'eliminazione della fase");
      }
    } else {
      // Fase locale non ancora salvata
      setProgrammazione({
        ...programmazione,
        fasi: programmazione.fasi.filter(f => f.id !== faseId)
      });
    }
  };

  const handleUpdateFase = (faseId, field, value) => {
    setProgrammazione({
      ...programmazione,
      fasi: programmazione.fasi.map(f => 
        f.id === faseId ? { ...f, [field]: value } : f
      )
    });
  };

  // =============================================
  // GESTIONE NOTE DI ATTENZIONE
  // =============================================
  
  const handleAddNotaAttenzione = async () => {
    if (!nuovaNota.nota.trim()) {
      toast.error("Inserisci il testo della nota");
      return;
    }
    
    setUploadingNota(true);
    try {
      const formData = new FormData();
      formData.append("nota", nuovaNota.nota);
      formData.append("data", nuovaNota.data);
      formData.append("testo_libero", nuovaNota.testo_libero);
      
      if (nuovaNota.allegato) {
        formData.append("allegato", nuovaNota.allegato);
      }
      
      const res = await axios.post(
        `${API}/commesse-pose/${id}/note-attenzione`,
        formData,
        { 
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" }
        }
      );
      
      setNoteAttenzione([...noteAttenzione, res.data]);
      setNuovaNota({
        nota: "",
        data: new Date().toISOString().split('T')[0],
        testo_libero: "",
        allegato: null
      });
      toast.success("Nota aggiunta");
    } catch (e) {
      toast.error("Errore nell'aggiunta della nota");
    } finally {
      setUploadingNota(false);
    }
  };
  
  const handleToggleNotaLetta = async (notaId, letta) => {
    try {
      await axios.put(
        `${API}/commesse-pose/${id}/note-attenzione/${notaId}`,
        { letta: !letta },
        { withCredentials: true }
      );
      setNoteAttenzione(noteAttenzione.map(n => 
        n.id === notaId ? { ...n, letta: !letta } : n
      ));
      toast.success(letta ? "Nota segnata come non letta" : "Nota segnata come letta");
    } catch (e) {
      toast.error("Errore nell'aggiornamento della nota");
    }
  };

  const handleToggleNascostaDaAttenzionati = async (notaId, attualmenteNascosta) => {
    try {
      const endpoint = attualmenteNascosta 
        ? `${API}/attenzionati/${id}/${notaId}/ripristina`
        : `${API}/attenzionati/${id}/${notaId}/nascondi`;
      
      await axios.put(endpoint, {}, { withCredentials: true });
      
      setNoteAttenzione(noteAttenzione.map(n => 
        n.id === notaId ? { ...n, nascosta_da_attenzionati: !attualmenteNascosta } : n
      ));
      
      toast.success(attualmenteNascosta 
        ? "Nota ripristinata nella pagina Attenzionati" 
        : "Nota rimossa dalla pagina Attenzionati"
      );
    } catch (e) {
      toast.error("Errore nell'aggiornamento della nota");
    }
  };
  
  const handleDeleteNotaAttenzione = async (notaId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questa nota?")) return;
    
    try {
      await axios.delete(
        `${API}/commesse-pose/${id}/note-attenzione/${notaId}`,
        { withCredentials: true }
      );
      setNoteAttenzione(noteAttenzione.filter(n => n.id !== notaId));
      toast.success("Nota eliminata");
    } catch (e) {
      toast.error("Errore nell'eliminazione della nota");
    }
  };
  
  // Funzione per eliminare un articolo ordinato
  const handleDeleteArticolo = (idx) => {
    const articolo = articoli[idx];
    const conferma = window.confirm(
      `Sei sicuro di voler eliminare l'articolo "${articolo.nome_articolo}"?\n\nQuesta azione non può essere annullata.`
    );
    
    if (!conferma) return;
    
    const newArticoli = articoli.filter((_, i) => i !== idx);
    setArticoli(newArticoli);
    toast.success(`Articolo "${articolo.nome_articolo}" eliminato`);
  };
  
  // Conta note non lette
  const noteNonLette = noteAttenzione.filter(n => !n.letta).length;

  const handleChiudiCommessa = async () => {
    // Validazioni
    const articoliTuttiTrasmessi = articoli.every(a => a.trasmesso);
    const fasiTutteComplete = programmazione.fasi.length === 0 || programmazione.fasi.every(f => f.completata);
    const saldoPagato = pagamentiStatus ? pagamentiStatus.residuo === 0 : true;
    
    if (!articoliTuttiTrasmessi) {
      toast.error("Non tutti gli articoli sono stati trasmessi");
      return;
    }
    
    if (programmazione.tipo_posa === "multiple" && !fasiTutteComplete) {
      toast.error("Non tutte le fasi sono completate");
      return;
    }
    
    if (!saldoPagato) {
      if (!window.confirm("⚠️ Il saldo non è stato completato. Vuoi comunque chiudere la commessa?")) {
        return;
      }
    }
    
    if (!window.confirm("Sei sicuro di voler chiudere definitivamente questa commessa?")) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/commesse-pose/${id}/chiusura`, chiusura, { withCredentials: true });
      toast.success("Commessa chiusa con successo!");
      fetchData();
    } catch (e) {
      toast.error("Errore nella chiusura della commessa");
    } finally {
      setSaving(false);
    }
  };

  const getArticoliInRitardo = () => {
    const oggi = new Date();
    return articoli.filter(a => {
      if (!a.trasmesso || !a.data_arrivo_deposito) return false;
      const dataArrivo = new Date(a.data_arrivo_deposito);
      const giorniDiff = Math.floor((oggi - dataArrivo) / (1000 * 60 * 60 * 24));
      return giorniDiff > 7 && !programmazione.data_posa;
    });
  };

  // Removed duplicate function - using the "posato" based version below

  // Badge per Status Ordine (basato su articoli trasmessi)
  const getOrdineStatusBadge = (articoli) => {
    if (!articoli || articoli.length === 0) {
      return <span className="px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">N/A</span>;
    }
    
    const trasmessi = articoli.filter(a => a.trasmesso).length;
    const totali = articoli.length;
    
    if (trasmessi === 0) {
      return <span className="px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">Da Trasmettere</span>;
    } else if (trasmessi < totali) {
      return <span className="px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">Parzialmente Trasmesso</span>;
    } else {
      return <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">Trasmesso</span>;
    }
  };

  // Calcolo percentuale completamento basata sugli articoli completati nella tab Programmazione
  const calcolaPercentualeCompletamento = () => {
    // Se il flag "Posato" globale è attivo, ritorna 100% a prescindere da tutto
    if (programmazione.posato) {
      return 100;
    }
    
    // Se non ci sono articoli, ritorna 0%
    if (!articoli || articoli.length === 0) {
      return 0;
    }
    
    if (programmazione.tipo_posa === "singola") {
      // In posa singola, calcola in base agli articoli flaggati come completati
      const articoliCompletati = Array.isArray(programmazione.articoli_completati) 
        ? programmazione.articoli_completati.length 
        : 0;
      
      if (articoliCompletati === 0) return 0;
      if (articoliCompletati >= articoli.length) return 100;
      
      return Math.round((articoliCompletati / articoli.length) * 100);
      
    } else if (programmazione.tipo_posa === "multiple") {
      // In posa multipla
      if (!programmazione.fasi || programmazione.fasi.length === 0) {
        return 0;
      }
      
      // Se TUTTE le fasi hanno flag "posato" o "completata", ritorna 100%
      const tutteFasiComplete = programmazione.fasi.every(fase => fase.posato || fase.completata);
      if (tutteFasiComplete) {
        return 100;
      }
      
      // Altrimenti calcola in base agli articoli con flag "Completato" nelle fasi
      const articoliCompletatiSet = new Set();
      
      programmazione.fasi.forEach(fase => {
        // Se la fase è posata/completata, tutti gli articoli della fase sono completati
        if (fase.posato || fase.completata) {
          if (Array.isArray(fase.articoli_ids)) {
            fase.articoli_ids.forEach(id => articoliCompletatiSet.add(id));
          }
        } else {
          // Altrimenti conta solo gli articoli con flag "Completato" attivo
          if (Array.isArray(fase.articoli_completati)) {
            fase.articoli_completati.forEach(id => articoliCompletatiSet.add(id));
          }
        }
      });
      
      const totaleArticoli = articoli.length;
      const numArticoliCompletati = articoliCompletatiSet.size;
      
      if (numArticoliCompletati >= totaleArticoli) {
        return 100;
      }
      
      return Math.round((numArticoliCompletati / totaleArticoli) * 100);
    }
    
    return 0;
  };

  // Ottieni fasi non posate
  const getFasiNonPosate = () => {
    if (programmazione.tipo_posa === "multiple") {
      return programmazione.fasi.filter(f => !f.posato);
    }
    return [];
  };

  const percentualeCompletamento = calcolaPercentualeCompletamento();
  const fasiNonPosate = getFasiNonPosate();
  
  // Calcola il numero di articoli completati per la visualizzazione
  const getArticoliCompletatiInfo = () => {
    if (!articoli || articoli.length === 0) {
      return { completati: 0, totale: 0 };
    }
    
    // Se posato globale è attivo, tutti gli articoli sono completati
    if (programmazione.posato) {
      return { completati: articoli.length, totale: articoli.length };
    }
    
    if (programmazione.tipo_posa === "singola") {
      // In posa singola, conta gli articoli flaggati come completati
      const completati = Array.isArray(programmazione.articoli_completati) 
        ? programmazione.articoli_completati.length 
        : 0;
      return { completati, totale: articoli.length };
    }
    
    // Per posa multipla
    if (programmazione.fasi) {
      // Se tutte le fasi sono complete, tutti gli articoli sono completati
      const tutteFasiComplete = programmazione.fasi.every(fase => fase.posato || fase.completata);
      if (tutteFasiComplete) {
        return { completati: articoli.length, totale: articoli.length };
      }
      
      // Altrimenti conta gli articoli con flag "Completato" nelle fasi
      const articoliCompletatiSet = new Set();
      programmazione.fasi.forEach(fase => {
        // Se la fase è posata/completata, tutti gli articoli della fase sono completati
        if (fase.posato || fase.completata) {
          if (Array.isArray(fase.articoli_ids)) {
            fase.articoli_ids.forEach(id => articoliCompletatiSet.add(id));
          }
        } else {
          // Altrimenti conta solo gli articoli con flag "Completato" attivo
          if (Array.isArray(fase.articoli_completati)) {
            fase.articoli_completati.forEach(id => articoliCompletatiSet.add(id));
          }
        }
      });
      return { completati: articoliCompletatiSet.size, totale: articoli.length };
    }
    
    return { completati: 0, totale: articoli.length };
  };
  
  const articoliCompletatiInfo = getArticoliCompletatiInfo();

  if (loading) {
    return (
      <PageLayout user={user} logout={logout} testId="commessa-detail-page">
        <div className="flex-1 flex items-center justify-center">
          <div className="spinner" />
        </div>
      </PageLayout>
    );
  }

  if (!commessa) {
    return (
      <PageLayout user={user} logout={logout} testId="commessa-detail-page">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-lg text-slate-600">Commessa non trovata</p>
            <Button onClick={() => navigate("/commesse-pose")} className="mt-4">
              Torna alla lista
            </Button>
          </div>
        </div>
      </PageLayout>
    );
  }

  const articoliInRitardo = getArticoliInRitardo();

  return (
    <PageLayout user={user} logout={logout} testId="commessa-detail-page" className="overflow-y-auto">
        <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
          
          {/* Banner commessa completata */}
          {commessa.completata && (
            <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 rounded-lg">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <div>
                  <p className="font-semibold text-green-900">Commessa completata il {formatDataItaliana(commessa.data_completamento)}</p>
                  {commessa.note_finali && <p className="text-sm text-green-700 mt-1">{commessa.note_finali}</p>}
                </div>
              </div>
            </div>
          )}

          {/* Header Commessa */}
          <div className="mb-6">
            <div className="flex items-center gap-2 text-sm text-slate-600 mb-4">
              <Link to="/commesse-pose" className="hover:text-blue-600">Commesse e Pose</Link>
              <span>/</span>
              <span>Dettaglio Commessa</span>
            </div>

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
                    {commessa.cliente_nome} {commessa.cliente_cognome}
                  </h1>
                  {!commessa.completata && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleDeleteCommessa}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                  <a href={`tel:${commessa.cliente_telefono}`} className="flex items-center gap-2 hover:text-blue-600">
                    <Phone className="w-4 h-4" />
                    {commessa.cliente_telefono || "N/D"}
                  </a>
                  {commessa.cliente_email && (
                    <a href={`mailto:${commessa.cliente_email}`} className="flex items-center gap-2 hover:text-blue-600">
                      <Mail className="w-4 h-4" />
                      {commessa.cliente_email}
                    </a>
                  )}
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {commessa.indirizzo || "N/D"}
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                {getOrdineStatusBadge(commessa.articoli)}
                <Button variant="outline" onClick={() => navigate("/commesse-pose")}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Torna alla lista
                </Button>
              </div>
            </div>
          </div>

          {/* Alert generali */}
          {articoliInRitardo.length > 0 && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-900">
                    {articoliInRitardo.length} articol{articoliInRitardo.length > 1 ? 'i' : 'o'} in ritardo
                  </p>
                  <p className="text-sm text-red-700 mt-1">
                    Articoli pronti da oltre 7 giorni senza posa programmata
                  </p>
                </div>
              </div>
            </div>
          )}

          {programmazione.data_posa && pagamenti.saldo_residuo > 0 && (
            <div className="mb-6 p-4 bg-orange-50 border-l-4 border-orange-500 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-orange-900">Attenzione: posa programmata ma saldo non completato</p>
                  <p className="text-sm text-orange-700 mt-1">
                    Saldo residuo: €{pagamenti.saldo_residuo.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-6">
            
            {/* SEZIONE NOTE GENERALI */}
            {!commessa.completata && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Note Commessa
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <textarea
                    value={noteGenerali}
                    onChange={(e) => setNoteGenerali(e.target.value)}
                    onBlur={handleSaveNoteGenerali}
                    className="w-full p-3 border rounded-md text-sm min-h-[100px]"
                    placeholder="Aggiungi note importanti per questa commessa..."
                  />
                  <div className="mt-2 flex justify-end">
                    <Button onClick={handleSaveNoteGenerali} size="sm" disabled={saving}>
                      <Save className="w-4 h-4 mr-2" />
                      Salva Note
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* SEZIONE ATTENZIONE - Note critiche e comunicazioni cliente */}
            <Card className={noteNonLette > 0 ? "border-2 border-orange-400 bg-orange-50/30" : ""}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className={`w-5 h-5 ${noteNonLette > 0 ? "text-orange-500" : "text-slate-500"}`} />
                    <span>Attenzione</span>
                    {noteNonLette > 0 && (
                      <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">
                        {noteNonLette} da leggere
                      </span>
                    )}
                  </div>
                  <span className="text-sm font-normal text-slate-500">
                    Note critiche e comunicazioni cliente
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Form per aggiungere nuova nota */}
                {!commessa.completata && (
                  <div className="bg-slate-50 p-4 rounded-lg mb-4 border border-slate-200">
                    <h4 className="font-medium text-slate-900 mb-3">Aggiungi nuova nota</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm">Nota *</Label>
                        <Input
                          value={nuovaNota.nota}
                          onChange={(e) => setNuovaNota({ ...nuovaNota, nota: e.target.value })}
                          placeholder="Descrizione breve della nota..."
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Data</Label>
                        <Input
                          type="date"
                          value={nuovaNota.data}
                          onChange={(e) => setNuovaNota({ ...nuovaNota, data: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <div className="mt-3">
                      <Label className="text-sm">Testo libero / Dettagli</Label>
                      <textarea
                        value={nuovaNota.testo_libero}
                        onChange={(e) => setNuovaNota({ ...nuovaNota, testo_libero: e.target.value })}
                        className="w-full p-2 border rounded-md text-sm min-h-[80px] mt-1"
                        placeholder="Inserisci dettagli aggiuntivi, comunicazioni del cliente, note critiche..."
                      />
                    </div>
                    <div className="mt-3 flex items-center gap-4">
                      <div className="flex-1">
                        <Label className="text-sm">Allegato (PDF, immagine, DOC, XLS)</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <FileDropZone
                            accept=".pdf,.png,.jpg,.jpeg,.gif,.doc,.docx,.xls,.xlsx"
                            dropLabel="Rilascia qui l'allegato"
                            onFile={(f) => setNuovaNota({ ...nuovaNota, allegato: f })}
                            className="flex-1"
                          >
                            <Input
                              type="file"
                              accept=".pdf,.png,.jpg,.jpeg,.gif,.doc,.docx,.xls,.xlsx"
                              onChange={(e) => setNuovaNota({ ...nuovaNota, allegato: e.target.files[0] })}
                              className="w-full"
                            />
                          </FileDropZone>
                          {nuovaNota.allegato && (
                            <span className="text-sm text-green-600 flex items-center gap-1">
                              <Paperclip className="w-4 h-4" />
                              {nuovaNota.allegato.name}
                            </span>
                          )}
                        </div>
                      </div>
                      <Button 
                        onClick={handleAddNotaAttenzione} 
                        disabled={uploadingNota || !nuovaNota.nota.trim()}
                        className="mt-6"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        {uploadingNota ? "Salvataggio..." : "Aggiungi Nota"}
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Lista note esistenti */}
                {noteAttenzione.length === 0 ? (
                  <div className="text-center py-6 text-slate-500">
                    <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    <p>Nessuna nota di attenzione presente</p>
                    <p className="text-sm mt-1">Aggiungi note critiche o comunicazioni del cliente</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {noteAttenzione.map((nota) => (
                      <div 
                        key={nota.id}
                        className={`p-4 rounded-lg border ${
                          nota.letta 
                            ? "bg-white border-slate-200" 
                            : "bg-orange-50 border-orange-300"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              {!nota.letta && (
                                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                              )}
                              <span className="font-medium text-slate-900">{nota.nota}</span>
                              <span className="text-xs text-slate-500">
                                {formatDataItaliana(nota.data)}
                              </span>
                            </div>
                            {nota.testo_libero && (
                              <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">
                                {nota.testo_libero}
                              </p>
                            )}
                            {nota.allegato && (() => {
                              // Correggi URL per retrocompatibilità (vecchi record potrebbero avere /uploads/ invece di /api/uploads/)
                              const fileUrl = nota.allegato.file_url.includes('/api/uploads/') 
                                ? nota.allegato.file_url 
                                : nota.allegato.file_url.replace('/uploads/', '/api/uploads/');
                              return (
                              <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <Paperclip className="w-4 h-4 text-blue-600" />
                                    <span className="text-sm font-medium text-blue-800">
                                      {nota.allegato.original_name}
                                    </span>
                                    <span className="text-xs text-blue-500 uppercase">
                                      ({nota.allegato.file_type})
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => window.open(fileUrl, '_blank')}
                                      className="text-blue-600 border-blue-300 hover:bg-blue-100"
                                    >
                                      <Eye className="w-4 h-4 mr-1" />
                                      Visualizza
                                    </Button>
                                    <a 
                                      href={fileUrl} 
                                      download={nota.allegato.original_name}
                                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
                                    >
                                      <Download className="w-4 h-4" />
                                      Scarica
                                    </a>
                                  </div>
                                </div>
                              </div>
                              );
                            })()}
                            <p className="text-xs text-slate-400 mt-2">
                              Creata da {nota.created_by} il {new Date(nota.created_at).toLocaleDateString('it-IT')}
                              {nota.nascosta_da_attenzionati && (
                                <span className="ml-2 px-2 py-0.5 bg-slate-200 text-slate-600 rounded text-xs">
                                  Nascosta da Attenzionati
                                </span>
                              )}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Pulsante per mostrare/nascondere in Attenzionati */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleNascostaDaAttenzionati(nota.id, nota.nascosta_da_attenzionati)}
                              title={nota.nascosta_da_attenzionati ? "Mostra in Attenzionati" : "Nascondi da Attenzionati"}
                              className={nota.nascosta_da_attenzionati ? "text-slate-400 hover:text-blue-600" : "text-blue-600 hover:text-blue-800"}
                            >
                              <List className="w-4 h-4" />
                            </Button>
                            {/* Pulsante letta/non letta */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleNotaLetta(nota.id, nota.letta)}
                              title={nota.letta ? "Segna come non letta" : "Segna come letta"}
                              className={nota.letta ? "text-slate-400" : "text-orange-600"}
                            >
                              {nota.letta ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </Button>
                            {!commessa.completata && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteNotaAttenzione(nota.id)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* SEZIONE 1: Status Ordine */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Package className="w-5 h-5" />
                    Articoli Ordinati
                  </div>
                  {!commessa.completata && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const nuovoArticolo = {
                          id: `art_${Date.now()}`,
                          tipo_articolo_id: "",
                          nome_articolo: "",
                          trasmesso: false,
                          data_prevista_ordine: "",
                          data_arrivo_deposito: "",
                          deposito: "",
                          codice_ddt: ""
                        };
                        setArticoli([...articoli, nuovoArticolo]);
                      }}
                      className="text-blue-600 border-blue-300 hover:bg-blue-50"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Aggiungi Articolo
                    </Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2 text-sm font-semibold">Tipo Articolo</th>
                        <th className="text-center p-2 text-sm font-semibold">Trasmesso</th>
                        <th className="text-left p-2 text-sm font-semibold">Data Prevista Ordine</th>
                        <th className="text-left p-2 text-sm font-semibold">Data Arrivo Deposito</th>
                        <th className="text-left p-2 text-sm font-semibold">Deposito</th>
                        <th className="text-left p-2 text-sm font-semibold">Codice DDT</th>
                        <th className="text-center p-2 text-sm font-semibold">Alert</th>
                        <th className="text-center p-2 text-sm font-semibold text-red-600">Cancella</th>
                      </tr>
                    </thead>
                    <tbody>
                      {articoli.map((art, idx) => {
                        const oggi = new Date();
                        let giorniInDeposito = 0;
                        let inRitardoDeposito = false;
                        let inRitardoOrdine = false;
                        let alertImminente = false;
                        
                        // Alert per deposito >7 giorni
                        if (art.trasmesso && art.data_arrivo_deposito) {
                          const dataArrivo = new Date(art.data_arrivo_deposito);
                          giorniInDeposito = Math.floor((oggi - dataArrivo) / (1000 * 60 * 60 * 24));
                          inRitardoDeposito = giorniInDeposito > 7 && !programmazione.data_posa;
                        }
                        
                        // Alert per ordine in ritardo - NON scatta se trasmesso=true
                        if (art.data_prevista_ordine && !art.trasmesso) {
                          const dataPrevista = new Date(art.data_prevista_ordine);
                          if (dataPrevista < oggi) {
                            inRitardoOrdine = true;
                          } else {
                            const giorniMancanti = Math.floor((dataPrevista - oggi) / (1000 * 60 * 60 * 24));
                            if (giorniMancanti <= 3 && !art.trasmesso) {
                              alertImminente = true;
                            }
                          }
                        }
                        
                        return (
                          <tr key={art.id || `articolo-${idx}`} className={`border-b ${inRitardoDeposito || inRitardoOrdine ? 'bg-red-50' : alertImminente ? 'bg-yellow-50' : ''}`}>
                            <td className="p-2">
                              <Input
                                value={art.nome_articolo || ""}
                                onChange={(e) => {
                                  const newArticoli = [...articoli];
                                  newArticoli[idx].nome_articolo = e.target.value;
                                  setArticoli(newArticoli);
                                }}
                                placeholder="Nome articolo"
                                className="font-medium text-slate-900 w-48"
                                disabled={commessa.completata}
                              />
                            </td>
                            <td className="p-2 text-center">
                              <input
                                type="checkbox"
                                checked={art.trasmesso}
                                onChange={() => handleAggiungiArticolo(idx)}
                                className="w-5 h-5 rounded cursor-pointer"
                                disabled={commessa.completata}
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                type="date"
                                value={art.data_prevista_ordine || ""}
                                onChange={(e) => {
                                  const newArticoli = [...articoli];
                                  newArticoli[idx].data_prevista_ordine = e.target.value;
                                  setArticoli(newArticoli);
                                }}
                                className="w-40"
                                disabled={commessa.completata}
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                type="date"
                                value={art.data_arrivo_deposito || ""}
                                onChange={(e) => {
                                  const newArticoli = [...articoli];
                                  newArticoli[idx].data_arrivo_deposito = e.target.value;
                                  setArticoli(newArticoli);
                                }}
                                className="w-40"
                                disabled={commessa.completata}
                              />
                            </td>
                            <td className="p-2">
                              <Select
                                value={art.deposito || ""}
                                onValueChange={(value) => {
                                  const newArticoli = [...articoli];
                                  newArticoli[idx].deposito = value;
                                  setArticoli(newArticoli);
                                }}
                                disabled={commessa.completata}
                              >
                                <SelectTrigger className="w-48">
                                  <SelectValue placeholder="Seleziona deposito" />
                                </SelectTrigger>
                                <SelectContent>
                                  {magazzini.length > 0 ? (
                                    magazzini.map((mag) => (
                                      <SelectItem key={mag.id} value={mag.nome}>
                                        {mag.nome}
                                      </SelectItem>
                                    ))
                                  ) : (
                                    <>
                                      <SelectItem value="Deposito Cuccu">Deposito Cuccu</SelectItem>
                                      <SelectItem value="Deposito Ingenio">Deposito Ingenio</SelectItem>
                                      <SelectItem value="Deposito Esterno">Deposito Esterno</SelectItem>
                                    </>
                                  )}
                                  <SelectItem value="In Transito">In Transito</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="p-2">
                              <Input
                                value={art.codice_ddt || ""}
                                onChange={(e) => {
                                  const newArticoli = [...articoli];
                                  newArticoli[idx].codice_ddt = e.target.value;
                                  setArticoli(newArticoli);
                                }}
                                placeholder="Codice DDT"
                                className="w-32"
                                disabled={commessa.completata}
                                data-testid={`codice-ddt-input-${idx}`}
                              />
                            </td>
                            <td className="p-2 text-center">
                              {inRitardoOrdine && (
                                <div className="flex flex-col items-center gap-1 text-red-600">
                                  <AlertTriangle className="w-5 h-5 animate-pulse" />
                                  <span className="text-xs font-medium">Ordine in ritardo!</span>
                                </div>
                              )}
                              {!inRitardoOrdine && alertImminente && (
                                <div className="flex flex-col items-center gap-1 text-yellow-600">
                                  <Clock className="w-5 h-5" />
                                  <span className="text-xs font-medium">Imminente</span>
                                </div>
                              )}
                              {inRitardoDeposito && (
                                <div className="flex flex-col items-center gap-1 text-red-600">
                                  <AlertTriangle className="w-5 h-5 animate-pulse" />
                                  <span className="text-xs font-medium">{giorniInDeposito}gg deposito</span>
                                </div>
                              )}
                            </td>
                            <td className="p-2 text-center">
                              {!commessa.completata && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDeleteArticolo(idx)}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                  title={`Elimina ${art.nome_articolo}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                
                {!commessa.completata && (
                  <div className="mt-4 flex justify-end">
                    <Button onClick={handleSaveArticoli} disabled={saving}>
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? "Salvataggio..." : "Salva Modifiche Articoli"}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* SEZIONE 2: Situazione Pagamenti e Contabili */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Euro className="w-5 h-5" />
                  Situazione Pagamenti e Contabili
                </CardTitle>
              </CardHeader>
              <CardContent>
                {pagamentiStatus ? (
                  <>
                    <div className={`p-6 rounded-lg border-2 ${
                      pagamentiStatus.in_regola 
                        ? "bg-green-50 border-green-500" 
                        : "bg-red-50 border-red-500"
                    }`}>
                      <div className="flex items-start gap-3 mb-4">
                        {pagamentiStatus.in_regola ? (
                          <CheckCircle className="w-6 h-6 text-green-600 mt-1" />
                        ) : (
                          <AlertTriangle className="w-6 h-6 text-red-600 mt-1 animate-pulse" />
                        )}
                        <div className="flex-1">
                          <p className={`text-lg font-bold mb-3 ${
                            pagamentiStatus.in_regola ? "text-green-900" : "text-red-900"
                          }`}>
                            {pagamentiStatus.in_regola 
                              ? (pagamentiStatus.regolarizzato_manualmente && !pagamentiStatus.in_regola_auto
                                  ? "✅ CLIENTE IN REGOLA COI PAGAMENTI (regolarizzato manualmente)"
                                  : "✅ CLIENTE IN REGOLA CON I PAGAMENTI")
                              : "❌ CLIENTE NON IN REGOLA CON I PAGAMENTI. VERIFICARE PRIMA DELLA POSA"}
                          </p>
                          
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                            <div>
                              <p className="text-sm text-slate-600">Importo totale</p>
                              <p className="text-xl font-bold">€{pagamentiStatus.totale.toFixed(2)}</p>
                              {pagamentiStatus.numero_box_pagamento > 1 && (
                                <p className="text-[11px] text-slate-500 italic">
                                  somma di {pagamentiStatus.numero_box_pagamento} box pagamento
                                </p>
                              )}
                            </div>
                            <div>
                              <p className="text-sm text-slate-600">Pagato</p>
                              <p className="text-xl font-bold text-green-600">€{pagamentiStatus.pagato.toFixed(2)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-slate-600">Residuo</p>
                              <p className={`text-xl font-bold ${
                                pagamentiStatus.residuo === 0 ? "text-green-600" : "text-red-600"
                              }`}>
                                €{pagamentiStatus.residuo.toFixed(2)}
                              </p>
                            </div>
                          </div>

                          {/* Breakdown per ogni box pagamento (visibile sempre se > 0 box) */}
                          {Array.isArray(pagamentiStatus.payments_breakdown) && pagamentiStatus.payments_breakdown.length > 0 && (
                            <div className="mb-4 space-y-2" data-testid="payments-breakdown">
                              <p className="text-sm font-semibold text-slate-700">
                                {pagamentiStatus.payments_breakdown.length === 1
                                  ? "Dettaglio Box Pagamento:"
                                  : `Dettaglio dei ${pagamentiStatus.payments_breakdown.length} Box Pagamento:`}
                              </p>
                              {pagamentiStatus.payments_breakdown.map((box, idx) => (
                                <div
                                  key={box.payment_id || `box-${idx}`}
                                  className="bg-white border border-slate-200 rounded-lg p-3"
                                  data-testid={`payment-box-${idx}`}
                                >
                                  <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                                    <p className="text-sm font-semibold text-slate-800">
                                      Box {idx + 1}
                                      {box.numero_contratto && (
                                        <span className="ml-2 text-xs font-normal text-slate-500">
                                          Contratto n. {box.numero_contratto}
                                        </span>
                                      )}
                                      {box.data_contratto && (
                                        <span className="ml-2 text-xs font-normal text-slate-500">
                                          · {box.data_contratto}
                                        </span>
                                      )}
                                    </p>
                                    <span className={`text-xs px-2 py-0.5 rounded ${box.residuo === 0 ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>
                                      {box.residuo === 0 ? "SALDATO" : `Residuo €${box.residuo.toFixed(2)}`}
                                    </span>
                                  </div>
                                  <div className="grid grid-cols-3 gap-2 text-xs mb-2">
                                    <div>
                                      <span className="text-slate-500">Totale</span>
                                      <p className="font-bold text-slate-800">€{box.importo_contratto.toFixed(2)}</p>
                                    </div>
                                    <div>
                                      <span className="text-slate-500">Pagato</span>
                                      <p className="font-bold text-green-700">€{box.pagato.toFixed(2)}</p>
                                    </div>
                                    <div>
                                      <span className="text-slate-500">Residuo</span>
                                      <p className={`font-bold ${box.residuo === 0 ? "text-green-700" : "text-red-700"}`}>€{box.residuo.toFixed(2)}</p>
                                    </div>
                                  </div>
                                  {Array.isArray(box.tranche) && box.tranche.length > 0 && (
                                    <div className="border-t border-slate-100 pt-2 space-y-1">
                                      {box.tranche.map((t) => (
                                        <div key={`${box.payment_id}-t${t.numero}`} className="flex items-center justify-between text-xs">
                                          <span className="flex items-center gap-1.5">
                                            <span className={`w-2 h-2 rounded-full ${t.pagata ? "bg-green-500" : "bg-slate-300"}`} />
                                            Tranche {t.numero}
                                            {t.numero_fattura && t.numero_fattura !== "N/A" && (
                                              <span className="text-slate-500 ml-1">· Fattura {t.numero_fattura}</span>
                                            )}
                                            {t.data_scadenza && (
                                              <span className="text-slate-400 ml-1">· {t.data_scadenza}</span>
                                            )}
                                          </span>
                                          <span className={`font-semibold ${t.pagata ? "text-green-700" : "text-slate-700"}`}>
                                            €{t.importo.toFixed(2)} {t.pagata ? "✓" : ""}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {pagamentiStatus.tranche_mancanti.length > 0 && (
                            <div className="mb-4">
                              <p className="font-semibold mb-2">Tranche mancanti:</p>
                              <ul className="list-disc list-inside space-y-1">
                                {pagamentiStatus.tranche_mancanti.map((tranche, idx) => (
                                  <li key={tranche.descrizione || `tranche-${idx}`} className="text-sm">
                                    {tranche.descrizione} (€{tranche.importo.toFixed(2)}) - Scadenza: {tranche.scadenza}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {/* Override manuale: checkbox "Cliente in Regola coi Pagamenti" + motivo */}
                          <div className="mt-4 pt-4 border-t border-slate-200 space-y-3">
                            {/* Banner auto-suggerimento se differenza < 1€ e non ancora regolarizzato */}
                            {!pagamentiStatus.regolarizzato_manualmente && pagamentiStatus.residuo > 0 && pagamentiStatus.residuo < 1 && (
                              <div className="p-3 bg-yellow-50 border-2 border-yellow-400 rounded-lg flex items-start gap-2" data-testid="banner-auto-regolarizza">
                                <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                                <div className="flex-1">
                                  <p className="text-sm font-semibold text-yellow-900 mb-1">
                                    Differenza minima rilevata: <span className="font-bold">€{pagamentiStatus.residuo.toFixed(2)}</span>
                                  </p>
                                  <p className="text-xs text-yellow-800 mb-2">
                                    Sembra una differenza di centesimi da bonifico bancario. Vuoi segnare automaticamente il cliente in regola?
                                  </p>
                                  <Button
                                    size="sm"
                                    data-testid="btn-auto-regolarizza"
                                    onClick={async () => {
                                      try {
                                        await axios.put(`${API}/pagamenti/regolarizza`, {
                                          cliente_nome: commessa.cliente_nome,
                                          cliente_cognome: commessa.cliente_cognome,
                                          regolarizzato: true,
                                          motivo: `Differenza automatica di €${pagamentiStatus.residuo.toFixed(2)} da bonifico bancario`,
                                        }, { withCredentials: true });
                                        toast.success("Cliente regolarizzato automaticamente");
                                        const res = await axios.get(`${API}/pagamenti/verifica-cliente`, {
                                          params: { cliente_nome: commessa.cliente_nome, cliente_cognome: commessa.cliente_cognome },
                                          withCredentials: true,
                                        });
                                        setPagamentiStatus(res.data);
                                      } catch (err) {
                                        const msg = err?.response?.data?.detail || err.message || "Errore";
                                        toast.error(`Errore: ${msg}`);
                                      }
                                    }}
                                    className="bg-yellow-600 hover:bg-yellow-700 text-white text-xs"
                                  >
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Sì, regolarizza automaticamente
                                  </Button>
                                </div>
                              </div>
                            )}
                            
                            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                              <input
                                type="checkbox"
                                data-testid="checkbox-regolarizza-pagamenti"
                                checked={!!pagamentiStatus.regolarizzato_manualmente}
                                onChange={async (e) => {
                                  const nextVal = e.target.checked;
                                  try {
                                    await axios.put(`${API}/pagamenti/regolarizza`, {
                                      cliente_nome: commessa.cliente_nome,
                                      cliente_cognome: commessa.cliente_cognome,
                                      regolarizzato: nextVal,
                                      motivo: nextVal ? (pagamentiStatus.regolarizzato_motivo || "") : "",
                                    }, { withCredentials: true });
                                    toast.success(nextVal
                                      ? "Cliente segnato in regola manualmente"
                                      : "Regolarizzazione manuale rimossa");
                                    const res = await axios.get(`${API}/pagamenti/verifica-cliente`, {
                                      params: { cliente_nome: commessa.cliente_nome, cliente_cognome: commessa.cliente_cognome },
                                      withCredentials: true,
                                    });
                                    setPagamentiStatus(res.data);
                                  } catch (err) {
                                    const msg = err?.response?.data?.detail || err.message || "Errore";
                                    toast.error(`Errore: ${msg}`);
                                  }
                                }}
                                className="w-4 h-4 accent-green-600"
                              />
                              <span className={`text-sm font-medium ${pagamentiStatus.regolarizzato_manualmente ? "text-green-700" : "text-slate-700"}`}>
                                Cliente in Regola coi Pagamenti (forza stato, ignora differenze di centesimi)
                              </span>
                            </label>
                            
                            {/* Campo motivo (visibile solo se checkbox attiva) */}
                            {pagamentiStatus.regolarizzato_manualmente && (
                              <div>
                                <Label htmlFor="motivo-regolarizzazione" className="text-xs text-slate-600 mb-1 block">
                                  Motivo della regolarizzazione (per traccia interna)
                                </Label>
                                <Input
                                  id="motivo-regolarizzazione"
                                  type="text"
                                  data-testid="input-motivo-regolarizzazione"
                                  placeholder='es. "differenza 3 centesimi da bonifico bancario"'
                                  defaultValue={pagamentiStatus.regolarizzato_motivo || ""}
                                  onBlur={async (e) => {
                                    const motivo = e.target.value.trim();
                                    if (motivo === (pagamentiStatus.regolarizzato_motivo || "")) return;
                                    try {
                                      await axios.put(`${API}/pagamenti/regolarizza`, {
                                        cliente_nome: commessa.cliente_nome,
                                        cliente_cognome: commessa.cliente_cognome,
                                        regolarizzato: true,
                                        motivo,
                                      }, { withCredentials: true });
                                      toast.success("Motivo salvato");
                                      const res = await axios.get(`${API}/pagamenti/verifica-cliente`, {
                                        params: { cliente_nome: commessa.cliente_nome, cliente_cognome: commessa.cliente_cognome },
                                        withCredentials: true,
                                      });
                                      setPagamentiStatus(res.data);
                                    } catch (err) {
                                      const msg = err?.response?.data?.detail || err.message || "Errore";
                                      toast.error(`Errore: ${msg}`);
                                    }
                                  }}
                                  onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                                  className="text-sm"
                                />
                              </div>
                            )}
                          </div>
                          
                        </div>
                      </div>
                    </div>
                    
                    {/* Warning contabili mancanti (se presenti) */}
                    {pagamentiStatus.in_regola && pagamentiStatus.contabili_mancanti.length > 0 && (
                      <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                          <div className="flex-1">
                            <p className="font-semibold text-yellow-900 mb-2">⚠️ Contabili da caricare:</p>
                            <ul className="list-disc list-inside space-y-1 text-sm text-yellow-800">
                              {pagamentiStatus.contabili_mancanti.map((contabile, idx) => (
                                <li key={typeof contabile === 'string' ? contabile : `contabile-${idx}`}>{contabile}</li>
                              ))}
                            </ul>
                            <p className="text-xs text-yellow-700 mt-2">Nota: Le contabili mancanti non bloccano la posa, ma è consigliato caricarle appena disponibili.</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="mt-4">
                      <Link 
                        to={`/payments?cliente=${commessa.cliente_nome} ${commessa.cliente_cognome}`}
                        className="text-sm text-blue-600 hover:text-blue-700 underline"
                      >
                        → Vai alla sezione Pagamenti per aggiornare
                      </Link>
                    </div>
                  </>
                ) : (
                  <div className="p-6 bg-slate-50 rounded-lg text-center">
                    <p className="text-slate-600">Caricamento status pagamenti...</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* SEZIONE 3: Programmazione Posa */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Programmazione e Assegnazione
                  {commessa.completata && commessa.data_completamento && (
                    <>
                      <span className="ml-2 px-3 py-1 bg-green-100 text-green-700 text-xs rounded">
                        ✓ Posa segnata Completata il {new Date(commessa.data_completamento).toLocaleDateString('it-IT', { 
                          day: '2-digit', 
                          month: '2-digit', 
                          year: 'numeric' 
                        })}
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        data-testid="riapri-posa-btn"
                        onClick={handleRiapriPosa}
                        disabled={saving}
                        className="ml-2 border-amber-400 text-amber-800 hover:bg-amber-50"
                      >
                        <RotateCcw className="w-4 h-4 mr-1.5" />
                        Riapri Posa
                      </Button>
                    </>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  
                  {/* Toggle Tipo Posa */}
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={programmazione.tipo_posa === "singola"}
                        onChange={() => {
                          // Quando si seleziona posa singola, seleziona automaticamente tutti gli articoli
                          const tuttiArticoliIds = articoli.map((art, idx) => art.id || `art_${idx}`);
                          setProgrammazione({ 
                            ...programmazione, 
                            tipo_posa: "singola",
                            articoli_ids: tuttiArticoliIds
                          });
                        }}
                        className="w-4 h-4"
                        disabled={commessa.completata}
                      />
                      <span className="text-sm font-medium">Posa Singola (tutti gli articoli)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={programmazione.tipo_posa === "multiple"}
                        onChange={() => setProgrammazione({ ...programmazione, tipo_posa: "multiple", fasi: [], articoli_ids: [] })}
                        className="w-4 h-4"
                        disabled={commessa.completata}
                      />
                      <span className="text-sm font-medium">Posa a Fasi Multiple</span>
                    </label>
                  </div>

                  {/* Posa Singola */}
                  {programmazione.tipo_posa === "singola" && (
                    <div className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Assegna Posatore</Label>
                          <Select 
                            value={programmazione.posatore_id || ""}
                            onValueChange={(value) => setProgrammazione({ ...programmazione, posatore_id: value })}
                            disabled={commessa.completata}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Seleziona posatore..." />
                            </SelectTrigger>
                            <SelectContent>
                              {posatori.map(p => (
                                <SelectItem key={p.id} value={p.id}>
                                  {p.nome} {p.cognome}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div>
                          <Label>Data Posa</Label>
                          <Input
                            type="date"
                            value={programmazione.data_posa}
                            onChange={(e) => setProgrammazione({ ...programmazione, data_posa: e.target.value })}
                            disabled={commessa.completata}
                          />
                        </div>
                        
                        <div>
                          <Label>Ora Posa (opzionale)</Label>
                        <Input
                          type="time"
                          value={programmazione.ora_posa}
                          onChange={(e) => setProgrammazione({ ...programmazione, ora_posa: e.target.value })}
                          disabled={commessa.completata}
                        />
                      </div>
                      </div>
                      
                      {/* Note di Posa */}
                      <div className="mt-4">
                        <Label>📝 Note di Posa</Label>
                        <Textarea
                          placeholder="Inserisci dettagli aggiuntivi per la posa (es. accesso, materiali da portare, ecc.)"
                          value={programmazione.note_posa}
                          onChange={(e) => setProgrammazione({ ...programmazione, note_posa: e.target.value })}
                          disabled={commessa.completata}
                          rows={3}
                          className="mt-1"
                        />
                      </div>
                      
                      {/* Selezione Articoli per Posa Singola con flag Completato */}
                      <div className="border rounded-lg p-4 bg-blue-50">
                        <Label className="text-sm font-semibold text-blue-900 mb-2 block">
                          📦 Articoli da Posare
                        </Label>
                        <div className="space-y-2">
                          {articoli.map((art, artIdx) => {
                            const articoloId = art.id || `art_${artIdx}`;
                            const isSelected = Array.isArray(programmazione.articoli_ids) && programmazione.articoli_ids.includes(articoloId);
                            const isCompletato = Array.isArray(programmazione.articoli_completati) && programmazione.articoli_completati.includes(articoloId);
                            
                            return (
                              <div key={articoloId} className={`flex items-center justify-between p-2 rounded ${isCompletato ? 'bg-green-100' : 'bg-white'}`}>
                                <div className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={(e) => {
                                      const currentIds = Array.isArray(programmazione.articoli_ids) ? programmazione.articoli_ids : [];
                                      const newIds = e.target.checked
                                        ? [...currentIds, articoloId]
                                        : currentIds.filter(id => id !== articoloId);
                                      setProgrammazione({ ...programmazione, articoli_ids: newIds });
                                    }}
                                    className="w-4 h-4"
                                    disabled={commessa.completata}
                                  />
                                  <span className={`text-sm font-medium ${isCompletato ? 'text-green-800' : ''}`}>
                                    {art.nome_articolo || "Articolo senza nome"}
                                    {art.codice_ddt && (
                                      <span className="ml-2 text-xs text-blue-600 font-normal">
                                        (DDT: {art.codice_ddt})
                                      </span>
                                    )}
                                  </span>
                                </div>
                                <label className="flex items-center gap-1 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={isCompletato}
                                    onChange={(e) => {
                                      const currentCompletati = Array.isArray(programmazione.articoli_completati) ? programmazione.articoli_completati : [];
                                      const newCompletati = e.target.checked
                                        ? [...currentCompletati, articoloId]
                                        : currentCompletati.filter(id => id !== articoloId);
                                      setProgrammazione({ ...programmazione, articoli_completati: newCompletati });
                                    }}
                                    className="w-4 h-4 accent-green-600"
                                    disabled={commessa.completata}
                                  />
                                  <span className={`text-xs ${isCompletato ? 'text-green-700 font-semibold' : 'text-slate-500'}`}>
                                    Completato
                                  </span>
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      
                      <div className="md:col-span-2">
                        <div className="flex items-center gap-3 p-4 border rounded-lg bg-green-50">
                          <input
                            type="checkbox"
                            id="posato_singola"
                            checked={programmazione.posato}
                            onChange={(e) => setProgrammazione({ ...programmazione, posato: e.target.checked })}
                            className="w-5 h-5"
                          />
                          <Label htmlFor="posato_singola" className="cursor-pointer font-semibold text-green-800">
                            ✓ Posato
                          </Label>
                          <span className="text-xs text-slate-600 ml-2">(Puoi togliere il flag in caso di errore)</span>
                        </div>
                      </div>
                        
                        <div className="md:col-span-2">
                          <div className="p-4 border rounded-lg bg-yellow-50 space-y-3">
                            <div className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                id="posa_incompleta"
                                checked={commessa?.posa_incompleta || false}
                                onChange={async (e) => {
                                  const checked = e.target.checked;
                                  try {
                                    await axios.put(
                                      `${API}/commesse-pose/${id}/posa-incompleta`,
                                      { 
                                        posa_incompleta: checked,
                                        note_posa_incompleta: commessa?.note_posa_incompleta || ""
                                      },
                                      { withCredentials: true }
                                    );
                                    setCommessa({ ...commessa, posa_incompleta: checked });
                                    toast.success("Flag posa incompleta aggiornato");
                                  } catch (e) {
                                    toast.error("Errore nell'aggiornamento");
                                  }
                                }}
                                className="w-5 h-5"
                              />
                              <Label htmlFor="posa_incompleta" className="cursor-pointer font-semibold text-yellow-800">
                                ⚠️ Posa Incompleta
                              </Label>
                              <span className="text-xs text-slate-600 ml-2">(Puoi togliere il flag in caso di errore)</span>
                            </div>
                            
                            <div>
                              <Label className="text-xs text-yellow-800">Note Posa Incompleta</Label>
                              <textarea
                                value={commessa?.note_posa_incompleta || ""}
                                onChange={(e) => setCommessa({ ...commessa, note_posa_incompleta: e.target.value })}
                                onBlur={async () => {
                                  try {
                                    await axios.put(
                                      `${API}/commesse-pose/${id}/posa-incompleta`,
                                      { 
                                        posa_incompleta: commessa?.posa_incompleta || false,
                                        note_posa_incompleta: commessa?.note_posa_incompleta || ""
                                      },
                                      { withCredentials: true }
                                    );
                                  } catch (e) {
                                    console.error("Error saving note:", e);
                                  }
                                }}
                                className="w-full p-2 border rounded-md text-sm"
                                rows="2"
                                placeholder="Inserisci note su problemi o motivi dell'incompletezza..."
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Posa a Fasi */}
                    {programmazione.tipo_posa === "multiple" && (
                      <div className="space-y-4">
                        {programmazione.fasi.map((fase, idx) => (
                          <div key={fase.id} className="p-4 border rounded-lg bg-slate-50">
                            <div className="flex items-center justify-between mb-3">
                              <Input
                                value={fase.nome}
                                onChange={(e) => handleUpdateFase(fase.id, "nome", e.target.value)}
                                className="flex-1 mr-3 font-medium"
                                placeholder="Nome fase..."
                                disabled={commessa.completata}
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleRimuoviFase(fase.id)}
                                className="text-red-600"
                                disabled={commessa.completata}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                            
                            <div className="grid md:grid-cols-2 gap-3 mb-3">
                              <div>
                                <Label className="text-xs">Posatore</Label>
                                <Select 
                                  value={fase.posatore_id || ""}
                                  onValueChange={(value) => handleUpdateFase(fase.id, "posatore_id", value)}
                                  disabled={commessa.completata}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Seleziona..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {posatori.map(p => (
                                      <SelectItem key={p.id} value={p.id}>
                                        {p.nome} {p.cognome}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              
                              <div>
                                <Label className="text-xs">Data</Label>
                                <Input
                                  type="date"
                                  value={fase.data || ""}
                                  onChange={(e) => handleUpdateFase(fase.id, "data", e.target.value)}
                                  disabled={commessa.completata}
                                />
                              </div>
                              
                              <div>
                                <Label className="text-xs">Ora (opzionale)</Label>
                                <Input
                                  type="time"
                                  value={fase.ora || ""}
                                  onChange={(e) => handleUpdateFase(fase.id, "ora", e.target.value)}
                                  disabled={commessa.completata}
                                />
                              </div>
                              
                              <div>
                                <Label className="text-xs">Tipo Fase</Label>
                                <Select 
                                  value={fase.tipo}
                                  onValueChange={(value) => handleUpdateFase(fase.id, "tipo", value)}
                                  disabled={commessa.completata}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="normale">Normale</SelectItem>
                                    <SelectItem value="rilievo">Rilievo</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            
                            <div className="space-y-3">
                              <div className="flex items-center gap-3">
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={fase.completata}
                                    onChange={(e) => handleUpdateFase(fase.id, "completata", e.target.checked)}
                                    className="w-4 h-4"
                                    disabled={commessa.completata}
                                  />
                                  <span className="text-sm">Fase completata</span>
                                </label>
                              </div>
                              
                              <div className="flex items-center gap-3 p-3 border rounded-lg bg-green-50">
                                <input
                                  type="checkbox"
                                  id={`posato_fase_${fase.id}`}
                                  checked={fase.posato || false}
                                  onChange={(e) => handleUpdateFase(fase.id, "posato", e.target.checked)}
                                  className="w-5 h-5"
                                />
                                <Label htmlFor={`posato_fase_${fase.id}`} className="cursor-pointer font-semibold text-green-800">
                                  ✓ Posato
                                </Label>
                                <span className="text-xs text-slate-600 ml-2">(Puoi togliere il flag in caso di errore)</span>
                              </div>
                            </div>
                            
                            {/* Selezione Articoli per questa Fase con flag Completato */}
                            <div className="border rounded-lg p-3 bg-blue-50 mb-3">
                              <Label className="text-xs font-semibold text-blue-900 mb-2 block">
                                📦 Articoli da Posare in questa Fase
                              </Label>
                              <div className="space-y-1">
                                {articoli.map((art, artIdx) => {
                                  const articoloId = art.id || `art_${artIdx}`;
                                  const isChecked = Array.isArray(fase.articoli_ids) && fase.articoli_ids.includes(articoloId);
                                  const isCompletato = Array.isArray(fase.articoli_completati) && fase.articoli_completati.includes(articoloId);
                                  
                                  return (
                                    <div key={articoloId} className={`flex items-center justify-between p-2 rounded ${isCompletato ? 'bg-green-100' : 'bg-white'}`}>
                                      <div className="flex items-center gap-2">
                                        <input
                                          type="checkbox"
                                          checked={isChecked}
                                          onChange={(e) => {
                                            const currentIds = Array.isArray(fase.articoli_ids) ? fase.articoli_ids : [];
                                            const newIds = e.target.checked
                                              ? [...currentIds, articoloId]
                                              : currentIds.filter(id => id !== articoloId);
                                            handleUpdateFase(fase.id, "articoli_ids", newIds);
                                          }}
                                          className="w-4 h-4"
                                          disabled={commessa.completata}
                                        />
                                        <span className={`text-xs font-medium ${isCompletato ? 'text-green-800' : ''}`}>
                                          {art.nome_articolo || "Articolo senza nome"}
                                          {art.codice_ddt && (
                                            <span className="ml-1 text-blue-600 font-normal">
                                              (DDT: {art.codice_ddt})
                                            </span>
                                          )}
                                        </span>
                                      </div>
                                      {isChecked && (
                                        <label className="flex items-center gap-1 cursor-pointer">
                                          <input
                                            type="checkbox"
                                            checked={isCompletato}
                                            onChange={(e) => {
                                              const currentCompletati = Array.isArray(fase.articoli_completati) ? fase.articoli_completati : [];
                                              const newCompletati = e.target.checked
                                                ? [...currentCompletati, articoloId]
                                                : currentCompletati.filter(id => id !== articoloId);
                                              handleUpdateFase(fase.id, "articoli_completati", newCompletati);
                                            }}
                                            className="w-4 h-4 accent-green-600"
                                            disabled={commessa.completata}
                                          />
                                          <span className={`text-xs ${isCompletato ? 'text-green-700 font-semibold' : 'text-slate-500'}`}>
                                            Completato
                                          </span>
                                        </label>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                            
                            <div>
                              <Label className="text-xs">Note</Label>
                              <textarea
                                value={fase.note || ""}
                                onChange={(e) => handleUpdateFase(fase.id, "note", e.target.value)}
                                className="w-full p-2 border rounded-md text-sm"
                                rows="2"
                                placeholder="Note per questa fase..."
                                disabled={commessa.completata}
                              />
                            </div>
                          </div>
                        ))}
                        
                        <Button 
                          variant="outline" 
                          onClick={handleAggiungiFase} 
                          className="w-full"
                          disabled={commessa.completata}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Aggiungi Fase
                        </Button>
                      </div>
                    )}

                    <div className="flex justify-end gap-2">
                      <Button onClick={handleSaveProgrammazione} disabled={saving}>
                        <Save className="w-4 h-4 mr-2" />
                        {saving ? "Salvataggio..." : "Salva Programmazione"}
                      </Button>
                      <Button 
                        onClick={handleCreateCalendarEvent} 
                        disabled={creatingCalendarEvent || !programmazione.posatore_id || !programmazione.data_posa}
                        variant="outline"
                        className="border-green-500 text-green-600 hover:bg-green-50"
                      >
                        <CalendarPlus className="w-4 h-4 mr-2" />
                        {creatingCalendarEvent ? "Creazione..." : "Crea Calendario"}
                      </Button>
                    </div>
                    
                    {/* Mostra link all'evento se esiste */}
                    {commessa.calendar_event_link && (
                      <div className="mt-2 text-right">
                        <a 
                          href={commessa.calendar_event_link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline flex items-center justify-end gap-1"
                        >
                          <Calendar className="w-4 h-4" />
                          Visualizza evento nel calendario ({commessa.calendar_posatore})
                        </a>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

            {/* SEZIONE 3.4: Trasporti e Consegne */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wrench className="w-5 h-5" />
                  Trasporti e Consegne
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div>
                    <Label>Servizio Trasporto</Label>
                    <Select
                      value={trasporto.facchinaggio_id || ""}
                      onValueChange={(value) => setTrasporto({ ...trasporto, facchinaggio_id: value === "none" ? null : value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleziona servizio trasporto..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Nessuno</SelectItem>
                        {trasporti.map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.nome}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Data Servizio</Label>
                      <Input
                        type="date"
                        value={trasporto.data_trasporto}
                        onChange={(e) => setTrasporto({ ...trasporto, data_trasporto: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Ora Servizio (opzionale)</Label>
                      <Input
                        type="time"
                        value={trasporto.ora_trasporto}
                        onChange={(e) => setTrasporto({ ...trasporto, ora_trasporto: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-4 border rounded-lg bg-slate-50">
                    <input
                      type="checkbox"
                      id="trasporta_posatore"
                      checked={trasporto.trasporta_posatore}
                      onChange={(e) => setTrasporto({ ...trasporto, trasporta_posatore: e.target.checked })}
                      className="w-5 h-5"
                    />
                    <div className="flex-1">
                      <Label htmlFor="trasporta_posatore" className="cursor-pointer font-semibold">
                        Trasporta il Posatore
                      </Label>
                      {trasporto.trasporta_posatore && programmazione.posatore_id && (
                        <p className="text-sm text-slate-600 mt-1">
                          Posatore: <span className="font-medium">{
                            posatori.find(p => p.id === programmazione.posatore_id)
                              ? `${posatori.find(p => p.id === programmazione.posatore_id).nome} ${posatori.find(p => p.id === programmazione.posatore_id).cognome}`
                              : "Non assegnato"
                          }</span>
                        </p>
                      )}
                      {trasporto.trasporta_posatore && !programmazione.posatore_id && (
                        <p className="text-sm text-amber-600 mt-1">
                          ⚠️ Nessun posatore assegnato nella sezione Programmazione
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <Label>Note</Label>
                    <textarea
                      value={trasporto.note_trasporto}
                      onChange={(e) => setTrasporto({ ...trasporto, note_trasporto: e.target.value })}
                      placeholder="Note aggiuntive sul trasporto e la consegna..."
                      className="w-full min-h-[120px] p-3 border rounded-lg resize-y"
                    />
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button onClick={handleSaveTrasporto} disabled={saving}>
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? "Salvataggio..." : "Salva Trasporto"}
                    </Button>
                    <Button 
                      onClick={handleCreateCalendarEventTrasporto} 
                      disabled={!trasporto.facchinaggio_id || !trasporto.data_trasporto}
                      variant="outline"
                      className="border-green-500 text-green-600 hover:bg-green-50"
                    >
                      <CalendarPlus className="w-4 h-4 mr-2" />
                      Crea Calendario
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* SEZIONE 3.5: Comunicazioni Cliente */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Comunicazioni Cliente
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  
                  {/* Dati Cliente da Pagamenti */}
                  {datiClientePagamenti && (
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm font-semibold text-slate-900 mb-3">Dati di Contatto (da Anagrafica Pagamenti)</p>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <Label className="text-xs text-slate-600">Cellulare</Label>
                          <p className="font-medium">{datiClientePagamenti.telefono || "Non disponibile"}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-600">Email</Label>
                          <p className="font-medium">{datiClientePagamenti.email || "Non disponibile"}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-600">Indirizzo</Label>
                          <p className="font-medium">
                            {datiClientePagamenti.indirizzo && datiClientePagamenti.citta 
                              ? `${datiClientePagamenti.indirizzo}, ${datiClientePagamenti.citta}${datiClientePagamenti.piano_consegna ? ` - ${datiClientePagamenti.piano_consegna}` : ''}`
                              : commessa.indirizzo || "Non disponibile"}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Status Avviso Cliente */}
                  <div className="space-y-3">
                    <Label className="text-base font-semibold">Status Avviso Cliente</Label>
                    <div className="flex flex-col gap-3">
                      <label className="flex items-center gap-3 cursor-pointer p-3 border rounded-lg hover:bg-slate-50">
                        <input
                          type="radio"
                          checked={comunicazioni.status_avviso_cliente === "da_avvisare"}
                          onChange={() => setComunicazioni({ ...comunicazioni, status_avviso_cliente: "da_avvisare" })}
                          className="w-4 h-4"
                          disabled={commessa.completata}
                        />
                        <div>
                          <span className="font-medium">Da Avvisare</span>
                          <p className="text-xs text-slate-600">Cliente non ancora contattato</p>
                        </div>
                      </label>
                      
                      <label className="flex items-center gap-3 cursor-pointer p-3 border rounded-lg hover:bg-slate-50">
                        <input
                          type="radio"
                          checked={comunicazioni.status_avviso_cliente === "chiamato_non_risposto"}
                          onChange={() => setComunicazioni({ ...comunicazioni, status_avviso_cliente: "chiamato_non_risposto" })}
                          className="w-4 h-4"
                          disabled={commessa.completata}
                        />
                        <div>
                          <span className="font-medium">Chiamato e Non Risposto</span>
                          <p className="text-xs text-slate-600">Tentativo di contatto senza risposta</p>
                        </div>
                      </label>
                      
                      <label className="flex items-center gap-3 cursor-pointer p-3 border rounded-lg hover:bg-slate-50">
                        <input
                          type="radio"
                          checked={comunicazioni.status_avviso_cliente === "avvisato"}
                          onChange={() => setComunicazioni({ ...comunicazioni, status_avviso_cliente: "avvisato" })}
                          className="w-4 h-4"
                          disabled={commessa.completata}
                        />
                        <div>
                          <span className="font-medium">Avvisato</span>
                          <p className="text-xs text-slate-600">Cliente correttamente informato</p>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Pulsante Invia Mail Dettagli */}
                  <div className="space-y-2 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-base font-semibold text-blue-800">Invia Mail Dettagli Posa</Label>
                        <p className="text-xs text-blue-600 mt-1">
                          Invia al cliente i dettagli della posa (data, orario, posatore, trasporto)
                        </p>
                      </div>
                      <Button 
                        onClick={handleInviaMailDettagli}
                        disabled={loadingPreview || sendingMailDettagli || commessa.completata || !programmazione.data_posa || !programmazione.posatore_id}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid="btn-invia-mail-dettagli"
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        {loadingPreview ? "Caricamento..." : "Componi Mail"}
                      </Button>
                    </div>
                    
                    {comunicazioni.mail_dettagli_inviata && comunicazioni.data_mail_dettagli && (
                      <div className="flex items-center gap-2 mt-2 p-2 bg-green-100 rounded border border-green-300">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                        <span className="text-sm text-green-700">
                          Mail inviata il {new Date(comunicazioni.data_mail_dettagli).toLocaleDateString("it-IT", {
                            day: "2-digit",
                            month: "2-digit", 
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit"
                          })}
                        </span>
                      </div>
                    )}
                    
                    {(!programmazione.data_posa || !programmazione.posatore_id) && (
                      <p className="text-xs text-amber-600 mt-1">
                        ⚠️ Compila prima data posa e posatore nella sezione Programmazione
                      </p>
                    )}
                  </div>

                  {/* Registro Mail Inviate */}
                  {commessa.registro_mail_dettagli && commessa.registro_mail_dettagli.length > 0 && (
                    <div className="space-y-2 p-4 bg-slate-50 rounded-lg border border-slate-200" data-testid="registro-mail-dettagli">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-base font-semibold text-slate-800 flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          Registro Mail Inviate
                        </Label>
                        <span className="text-xs text-slate-500 bg-white px-2 py-0.5 rounded-full border">
                          {commessa.registro_mail_dettagli.length} {commessa.registro_mail_dettagli.length === 1 ? "invio" : "invii"}
                        </span>
                      </div>
                      <div className="space-y-2">
                        {[...commessa.registro_mail_dettagli].reverse().map((log) => (
                          <div
                            key={log.id}
                            className="flex items-center justify-between gap-3 bg-white p-3 rounded border border-slate-200 hover:border-blue-300 transition-colors"
                            data-testid={`mail-log-entry-${log.id}`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
                                <Clock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                <span>
                                  {new Date(log.sent_at).toLocaleDateString("it-IT", {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit"
                                  })}
                                </span>
                              </div>
                              <div className="text-xs text-slate-500 mt-1 truncate">
                                → {log.to}
                                {log.data_posa && <span className="ml-2">· Posa: {log.data_posa}{log.ora_posa ? ` ${log.ora_posa}` : ""}</span>}
                                {log.articoli_count > 0 && <span className="ml-2">· {log.articoli_count} art.</span>}
                              </div>
                              {log.sent_by && (
                                <div className="text-xs text-slate-400 mt-0.5">Inviata da: {log.sent_by}</div>
                              )}
                            </div>
                            <div className="flex gap-1 shrink-0">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setViewingMailLog(log)}
                                data-testid={`view-mail-log-${log.id}`}
                              >
                                <Eye className="w-3.5 h-3.5 mr-1" />
                                Leggi
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  const url = `${API}/commesse-pose/${id}/mail-dettagli-log/${log.id}/pdf`;
                                  const link = document.createElement("a");
                                  link.href = url;
                                  link.setAttribute("download", "");
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                }}
                                data-testid={`pdf-mail-log-${log.id}`}
                                title="Scarica PDF"
                              >
                                <Download className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Note Importanti */}
                  <div className="space-y-2">
                    <Label className="text-base font-semibold">Note Importanti</Label>
                    <textarea
                      value={comunicazioni.note_avviso_cliente}
                      onChange={(e) => setComunicazioni({ ...comunicazioni, note_avviso_cliente: e.target.value })}
                      className="w-full p-3 border rounded-md text-sm min-h-[100px]"
                      placeholder="Aggiungi note importanti sulla comunicazione con il cliente (es: preferenze orari, richieste particolari, ecc.)"
                      disabled={commessa.completata}
                    />
                  </div>

                  {!commessa.completata && (
                    <div className="flex justify-end">
                      <Button onClick={handleSaveComunicazioni} disabled={saving}>
                        <Save className="w-4 h-4 mr-2" />
                        {saving ? "Salvataggio..." : "Salva Comunicazioni"}
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* SEZIONE 4: Tracking Avanzamento */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Stato Avanzamento Lavori
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Completamento</span>
                        <span className="text-xs text-slate-500">
                          ({articoliCompletatiInfo.completati}/{articoliCompletatiInfo.totale} articoli posati)
                        </span>
                      </div>
                      <span className="text-sm font-bold">{percentualeCompletamento}%</span>
                    </div>
                    <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all ${percentualeCompletamento === 100 ? 'bg-green-500' : 'bg-blue-500'}`}
                        style={{ width: `${percentualeCompletamento}%` }}
                      />
                    </div>
                  </div>

                  {programmazione.tipo_posa === "multiple" && programmazione.fasi.length > 0 && (
                    <div>
                      <Label className="text-sm font-semibold mb-2 block">Fasi</Label>
                      <div className="space-y-2">
                        {programmazione.fasi.map(fase => (
                          <div key={fase.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              {fase.completata ? (
                                <CheckCircle className="w-5 h-5 text-green-600" />
                              ) : fase.data && new Date(fase.data) < new Date() ? (
                                <AlertTriangle className="w-5 h-5 text-orange-600" />
                              ) : (
                                <Clock className="w-5 h-5 text-slate-400" />
                              )}
                              <span className="font-medium">{fase.nome}</span>
                            </div>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              fase.completata 
                                ? "bg-green-100 text-green-700"
                                : fase.data && new Date(fase.data) < new Date()
                                  ? "bg-orange-100 text-orange-700"
                                  : "bg-slate-200 text-slate-600"
                            }`}>
                              {fase.completata ? "Completata" : fase.data && new Date(fase.data) < new Date() ? "In Ritardo" : "Da fare"}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {!commessa.completata && (
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm font-semibold text-blue-900 mb-2">Cosa manca da fare:</p>
                      {percentualeCompletamento === 100 ? (
                        <p className="text-sm font-bold text-green-700">
                          ✅ Tutte le Pose sono State Completate: puoi chiudere la Pratica!
                        </p>
                      ) : (
                        <ul className="text-sm text-blue-700 space-y-1">
                          {programmazione.tipo_posa === "singola" && !programmazione.posato && (
                            <li>• Posa non ancora effettuata</li>
                          )}
                          {programmazione.tipo_posa === "multiple" && fasiNonPosate.length > 0 && (
                            <li>• {fasiNonPosate.length} fas{fasiNonPosate.length > 1 ? 'i' : 'e'} non posat{fasiNonPosate.length > 1 ? 'e' : 'a'}: {fasiNonPosate.map((f, i) => `${f.nome || `Fase ${i+1}`}`).join(', ')}</li>
                          )}
                          {articoli.filter(a => !a.trasmesso).length > 0 && (
                            <li>• {articoli.filter(a => !a.trasmesso).length} articol{articoli.filter(a => !a.trasmesso).length > 1 ? 'i' : 'o'} non trasmess{articoli.filter(a => !a.trasmesso).length > 1 ? 'i' : 'o'}</li>
                          )}
                          {pagamentiStatus && pagamentiStatus.residuo > 0 && (
                            <li>• Saldo residuo: €{pagamentiStatus.residuo.toFixed(2)}</li>
                          )}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* SEZIONE 5: Chiusura Commessa */}
            {!commessa.completata && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    Chiusura Lavori
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={commessa.completata}
                          className="w-5 h-5"
                          disabled
                        />
                        <div>
                          <span className="font-semibold text-slate-900 block">Lavoro Completato Definitivamente</span>
                          <span className="text-sm text-slate-600">
                            Tutte le fasi devono essere complete, articoli pronti e saldo pagato
                          </span>
                        </div>
                      </label>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Data Chiusura</Label>
                        <Input
                          type="date"
                          value={chiusura.data_chiusura}
                          onChange={(e) => setChiusura({ ...chiusura, data_chiusura: e.target.value })}
                        />
                      </div>
                      
                      <div className="flex items-end">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={chiusura.invia_recensione}
                            onChange={(e) => setChiusura({ ...chiusura, invia_recensione: e.target.checked })}
                            className="w-4 h-4"
                          />
                          <span className="text-sm">Invia richiesta recensione al cliente</span>
                        </label>
                      </div>
                    </div>

                    <div>
                      <Label>Note Finali</Label>
                      <textarea
                        value={chiusura.note_finali}
                        onChange={(e) => setChiusura({ ...chiusura, note_finali: e.target.value })}
                        className="w-full p-3 border rounded-md"
                        rows="3"
                        placeholder="Note sulla chiusura dei lavori..."
                      />
                    </div>

                    <div className="flex justify-end">
                      <Button 
                        onClick={handleChiudiCommessa} 
                        disabled={saving}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        {saving ? "Chiusura..." : "Conferma Chiusura Commessa"}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* SEZIONE 6: Spese e Margini */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Euro className="w-5 h-5" />
                  Spese e Margini
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  
                  {/* Sezione Spese */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-semibold text-red-800">Spese</h3>
                      <Button size="sm" onClick={handleAggiungiSpesa} variant="outline">
                        <Plus className="w-4 h-4 mr-2" />
                        Aggiungi Spesa
                      </Button>
                    </div>
                    
                    <div className="space-y-2">
                      {speseMargini.spese.map((spesa) => (
                        <div key={spesa.id} className="p-3 border rounded-lg bg-red-50">
                          <div className="grid md:grid-cols-5 gap-3 items-end">
                            <div>
                              <Label className="text-xs">Descrizione</Label>
                              <Input
                                value={spesa.descrizione}
                                onChange={(e) => handleUpdateSpesa(spesa.id, 'descrizione', e.target.value)}
                                placeholder="Es: Materiali..."
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Imponibile (€)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={spesa.importo_imponibile}
                                onChange={(e) => handleUpdateSpesa(spesa.id, 'importo_imponibile', e.target.value)}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">IVA (%)</Label>
                              <Input
                                type="number"
                                step="1"
                                value={spesa.iva_percentuale}
                                onChange={(e) => handleUpdateSpesa(spesa.id, 'iva_percentuale', e.target.value)}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Importo Ivato (€)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={spesa.importo_ivato.toFixed(2)}
                                disabled
                                className="bg-gray-100"
                              />
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRimuoviSpesa(spesa.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      {speseMargini.spese.length === 0 && (
                        <p className="text-sm text-gray-500 text-center py-4">Nessuna spesa registrata</p>
                      )}
                    </div>
                  </div>

                  {/* Sezione Incassi */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-semibold text-green-800">Incassi</h3>
                      <Button size="sm" onClick={handleAggiungiIncasso} variant="outline">
                        <Plus className="w-4 h-4 mr-2" />
                        Aggiungi Incasso
                      </Button>
                    </div>
                    
                    <div className="space-y-2">
                      {speseMargini.incassi.map((incasso) => (
                        <div key={incasso.id} className="p-3 border rounded-lg bg-green-50">
                          <div className="grid md:grid-cols-5 gap-3 items-end">
                            <div>
                              <Label className="text-xs">Descrizione</Label>
                              <Input
                                value={incasso.descrizione}
                                onChange={(e) => handleUpdateIncasso(incasso.id, 'descrizione', e.target.value)}
                                placeholder="Es: Pagamento cliente..."
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Imponibile (€)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={incasso.importo_imponibile}
                                onChange={(e) => handleUpdateIncasso(incasso.id, 'importo_imponibile', e.target.value)}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">IVA (%)</Label>
                              <Input
                                type="number"
                                step="1"
                                value={incasso.iva_percentuale}
                                onChange={(e) => handleUpdateIncasso(incasso.id, 'iva_percentuale', e.target.value)}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Importo Ivato (€)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={incasso.importo_ivato.toFixed(2)}
                                disabled
                                className="bg-gray-100"
                              />
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRimuoviIncasso(incasso.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      {speseMargini.incassi.length === 0 && (
                        <p className="text-sm text-gray-500 text-center py-4">Nessun incasso registrato</p>
                      )}
                    </div>
                  </div>

                  {/* Sezione Bilancio */}
                  <div className="p-4 border-2 rounded-lg bg-slate-50">
                    <h3 className="text-lg font-semibold mb-4">Bilancio</h3>
                    <div className="grid md:grid-cols-4 gap-4 mb-4">
                      <div className="p-3 bg-red-100 rounded-lg">
                        <p className="text-xs text-red-800 font-medium">Totale Spese</p>
                        <p className="text-xl font-bold text-red-900">€ {calcolaTotaleSpese().toFixed(2)}</p>
                      </div>
                      <div className="p-3 bg-green-100 rounded-lg">
                        <p className="text-xs text-green-800 font-medium">Totale Incassi</p>
                        <p className="text-xl font-bold text-green-900">€ {calcolaTotaleIncassi().toFixed(2)}</p>
                      </div>
                      <div className={`p-3 rounded-lg ${calcolaBilancio() >= 0 ? 'bg-blue-100' : 'bg-orange-100'}`}>
                        <p className={`text-xs font-medium ${calcolaBilancio() >= 0 ? 'text-blue-800' : 'text-orange-800'}`}>Bilancio</p>
                        <p className={`text-xl font-bold ${calcolaBilancio() >= 0 ? 'text-blue-900' : 'text-orange-900'}`}>
                          € {calcolaBilancio().toFixed(2)}
                        </p>
                      </div>
                      <div className={`p-3 rounded-lg ${parseFloat(calcolaPercentualeProfitto()) >= 0 ? 'bg-purple-100' : 'bg-orange-100'}`}>
                        <p className={`text-xs font-medium ${parseFloat(calcolaPercentualeProfitto()) >= 0 ? 'text-purple-800' : 'text-orange-800'}`}>% Profitto</p>
                        <p className={`text-xl font-bold ${parseFloat(calcolaPercentualeProfitto()) >= 0 ? 'text-purple-900' : 'text-orange-900'}`}>
                          {calcolaPercentualeProfitto()}%
                        </p>
                      </div>
                    </div>

                    {/* Grafici */}
                    {(speseMargini.spese.length > 0 || speseMargini.incassi.length > 0) && (
                      <div className="grid md:grid-cols-2 gap-6 mt-6">
                        {/* Grafico a Torta - Spese */}
                        {speseMargini.spese.length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold mb-3 text-center">Distribuzione Spese</h4>
                            <ResponsiveContainer width="100%" height={250}>
                              <PieChart>
                                <Pie
                                  data={speseMargini.spese.map(s => ({
                                    name: s.descrizione || 'Senza descrizione',
                                    value: parseFloat(s.importo_ivato)
                                  }))}
                                  cx="50%"
                                  cy="50%"
                                  labelLine={false}
                                  label={(entry) => `${entry.name}: €${entry.value.toFixed(0)}`}
                                  outerRadius={80}
                                  fill="#8884d8"
                                  dataKey="value"
                                >
                                  {speseMargini.spese.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={['#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16'][index % 5]} />
                                  ))}
                                </Pie>
                                <Tooltip formatter={(value) => `€${value.toFixed(2)}`} />
                              </PieChart>
                            </ResponsiveContainer>
                          </div>
                        )}

                        {/* Grafico a Barre - Confronto */}
                        <div>
                          <h4 className="text-sm font-semibold mb-3 text-center">Confronto Spese vs Incassi</h4>
                          <ResponsiveContainer width="100%" height={250}>
                            <BarChart data={[
                              { name: 'Spese', valore: calcolaTotaleSpese() },
                              { name: 'Incassi', valore: calcolaTotaleIncassi() }
                            ]}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" />
                              <YAxis />
                              <Tooltip formatter={(value) => `€${value.toFixed(2)}`} />
                              <Legend />
                              <Bar dataKey="valore" fill="#8884d8">
                                {[
                                  <Cell key="spese" fill="#ef4444" />,
                                  <Cell key="incassi" fill="#22c55e" />
                                ]}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end">
                    <Button onClick={handleSaveSpeseMargin} disabled={saving}>
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? "Salvataggio..." : "Salva Spese e Margini"}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

          </div>
        </div>

        {/* Modal Compositore Mail (edit prima dell'invio) */}
        {mailDraft && (
          <div
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
            onClick={() => !sendingMailDettagli && setMailDraft(null)}
            data-testid="mail-compose-modal"
          >
            <div
              className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[92vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between p-5 border-b bg-blue-50 rounded-t-xl">
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-blue-700 uppercase tracking-wider font-semibold mb-1">Componi Mail Dettagli Posa</div>
                  <div className="text-sm text-slate-600 flex flex-wrap gap-x-4 gap-y-1 mt-1">
                    <span><span className="text-slate-400">A:</span> {mailDraft.to}</span>
                    <span><span className="text-slate-400">BCC:</span> {mailDraft.bcc}</span>
                    {mailDraft.articoli_count > 0 && (
                      <span className="bg-amber-100 text-amber-800 px-2 rounded-full text-xs font-semibold">
                        📦 {mailDraft.articoli_count} articoli inclusi
                      </span>
                    )}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => !sendingMailDettagli && setMailDraft(null)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="flex-1 overflow-auto p-5 space-y-4 bg-slate-50">
                {/* Oggetto editabile */}
                <div>
                  <Label className="text-xs font-semibold text-slate-700 mb-1 block">Oggetto</Label>
                  <Input
                    value={mailDraft.subject}
                    onChange={(e) => setMailDraft({ ...mailDraft, subject: e.target.value })}
                    className="bg-white"
                    data-testid="mail-compose-subject"
                  />
                </div>

                {/* Tabs Anteprima / Testo Plain */}
                <div>
                  <Label className="text-xs font-semibold text-slate-700 mb-1 block">Anteprima Email (HTML)</Label>
                  <iframe
                    title="Anteprima email"
                    srcDoc={mailDraft.body_html}
                    className="w-full h-[45vh] bg-white rounded border-0 shadow-sm"
                    sandbox=""
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    💡 L&apos;HTML viene generato automaticamente. Per personalizzare il contenuto, modifica il testo semplice qui sotto (verrà usato come fallback).
                  </p>
                </div>

                {/* Testo plain editabile */}
                <div>
                  <Label className="text-xs font-semibold text-slate-700 mb-1 block">Testo semplice (editabile)</Label>
                  <Textarea
                    value={mailDraft.body_text}
                    onChange={(e) => setMailDraft({ ...mailDraft, body_text: e.target.value })}
                    className="w-full min-h-[240px] font-mono text-xs bg-white"
                    data-testid="mail-compose-body-text"
                  />
                </div>
              </div>

              <div className="flex justify-between items-center gap-2 p-4 border-t bg-slate-50 rounded-b-xl">
                <div className="text-xs text-slate-500">
                  ℹ️ Verrà creata una <b>bozza in Gmail</b> (non inviata automaticamente) con copia BCC a info@showroomingenio.it
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setMailDraft(null)} disabled={sendingMailDettagli}>
                    Annulla
                  </Button>
                  <Button
                    onClick={confirmSendMailDettagli}
                    disabled={sendingMailDettagli || !mailDraft.subject.trim()}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="btn-confirm-invia-mail"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    {sendingMailDettagli ? "Creazione bozza..." : "Crea Bozza Gmail"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal Visualizzatore Registro Mail Inviate */}
        {viewingMailLog && (
          <div
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
            onClick={() => setViewingMailLog(null)}
            data-testid="mail-log-modal"
          >
            <div
              className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-start justify-between p-5 border-b bg-slate-50 rounded-t-xl">
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1">Registro Mail Inviate</div>
                  <div className="text-lg font-bold text-slate-900 truncate">{viewingMailLog.subject}</div>
                  <div className="text-sm text-slate-600 mt-2 flex flex-wrap gap-x-4 gap-y-1">
                    <span><span className="text-slate-400">A:</span> {viewingMailLog.to}</span>
                    {viewingMailLog.bcc && <span><span className="text-slate-400">BCC:</span> {viewingMailLog.bcc}</span>}
                    <span>
                      <span className="text-slate-400">Inviata:</span>{" "}
                      {new Date(viewingMailLog.sent_at).toLocaleDateString("it-IT", {
                        day: "2-digit", month: "2-digit", year: "numeric",
                        hour: "2-digit", minute: "2-digit"
                      })}
                    </span>
                    {viewingMailLog.sent_by && (
                      <span><span className="text-slate-400">Da:</span> {viewingMailLog.sent_by}</span>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setViewingMailLog(null)}
                  data-testid="close-mail-log-modal"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              {/* Body */}
              <div className="flex-1 overflow-auto p-2 bg-slate-100">
                {viewingMailLog.body_html ? (
                  <iframe
                    title="Anteprima email"
                    srcDoc={viewingMailLog.body_html}
                    className="w-full h-[65vh] bg-white rounded border-0"
                    sandbox=""
                  />
                ) : (
                  <pre className="whitespace-pre-wrap text-sm p-4 bg-white rounded font-mono text-slate-700">
                    {viewingMailLog.body_text}
                  </pre>
                )}
              </div>
              {/* Footer */}
              <div className="flex justify-end gap-2 p-4 border-t bg-slate-50 rounded-b-xl">
                <Button
                  variant="outline"
                  onClick={() => {
                    const url = `${API}/commesse-pose/${id}/mail-dettagli-log/${viewingMailLog.id}/pdf`;
                    const link = document.createElement("a");
                    link.href = url;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  data-testid="download-mail-log-pdf"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Scarica PDF
                </Button>
                <Button variant="outline" onClick={() => setViewingMailLog(null)}>Chiudi</Button>
              </div>
            </div>
          </div>
        )}
    </PageLayout>
  );
}
